import React, { useState, useEffect, useCallback, useRef } from 'react';
import CampoDataHora from '../components/CampoDataHora';
import { useNavigate } from 'react-router-dom';
import { ModalDemanda, TIPOS_DEMANDA, PRAZO_DIAS } from './Demandas';
import { UF_GRID, UF_NOME } from './DashboardRevista';
import SelectColaborador from '../components/SelectColaborador';
import DicaLinhas, { DicaBarras, variacao } from '../components/DicaLinhas';

// Escala verde do Mapa da Carteira (mais clientes = mais escuro)
function corVerde(n, max) {
  if (!n) return { bg: '#f3f4f6', texto: '#9ca3af' };
  const r = n / (max || 1);
  if (r < 0.15) return { bg: '#dcfce7', texto: '#14532d' };
  if (r < 0.35) return { bg: '#86efac', texto: '#14532d' };
  if (r < 0.6) return { bg: '#22c55e', texto: '#fff' };
  if (r < 0.85) return { bg: '#15803d', texto: '#fff' };
  return { bg: '#14532d', texto: '#fff' };
}

// 🗺️ Aba: mapa do Brasil com a carteira por UF (clientes ou processos);
// clique no estado abre a lista de marcas dele
function AbaMapaCarteira() {
  const navigate = useNavigate();
  const irCliente = (id) => id && navigate(`/clientes/${id}/editar`);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [modo, setModo] = useState('clientes'); // 'clientes' | 'processos'
  const [ufDetalhe, setUfDetalhe] = useState(null);
  const [buscaMarca, setBuscaMarca] = useState('');
  const [exportando, setExportando] = useState(false);

  // 📄 Exporta o mapa + painel em PDF (paisagem), no modo atual
  const exportarPdf = async () => {
    if (exportando) return;
    setExportando(true);
    try {
      const r = await fetch(`/sucesso-cliente/mapa-carteira-pdf?modo=${modo}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!r.ok) {
        let det = ''; try { det = (await r.json()).erro || ''; } catch { /* não-JSON */ }
        return alert(`Falha ao exportar (HTTP ${r.status}). ${det}`.trim());
      }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = `Mapa da carteira (${modo}) — Preserva Marcas.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Falha ao exportar o PDF: ' + (e?.message || 'erro de rede'));
    } finally {
      setExportando(false);
    }
  };
  useEffect(() => {
    apiFetch('sucesso-cliente/mapa-carteira').then(d => d.erro ? setErro(d.erro) : setDados(d)).catch(e => setErro(e.message));
  }, []);

  const porUf = dados?.por_uf || {};
  const valor = (uf) => porUf[uf]?.[modo] || 0;
  const max = Math.max(1, ...Object.keys(porUf).map(valor));
  const ranking = Object.keys(porUf).map(uf => [uf, valor(uf)]).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const rotulo = modo === 'clientes' ? 'cliente(s)' : 'processo(s)';

  const marcasUf = ufDetalhe ? (dados?.marcas_por_uf?.[ufDetalhe] || []) : [];
  const marcasFiltradas = buscaMarca
    ? marcasUf.filter(m => (m.marca + ' ' + m.cliente + ' ' + m.numero).toLowerCase().includes(buscaMarca.toLowerCase()))
    : marcasUf;

  return (
    <div>
      <div style={{ background: '#fff', border: '1px solid #c8d0da', borderRadius: 8, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: 0 }}>🗺️ Mapa da carteira — onde estão os clientes da Preserva</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', border: '1px solid #bbf7d0', borderRadius: 6, overflow: 'hidden' }}>
              {[['clientes', '👥 Clientes'], ['processos', '™ Processos']].map(([k, lbl]) => (
                <button key={k} onClick={() => setModo(k)} style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: modo === k ? '#15803d' : '#fff', color: modo === k ? '#fff' : '#374151',
                }}>{lbl}</button>
              ))}
            </div>
            <button onClick={exportarPdf} disabled={exportando}
              title="Baixa o mapa com o painel de números em PDF (paisagem)"
              style={{ background: exportando ? '#e5e7eb' : '#0a1628', color: exportando ? '#6b7280' : '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: exportando ? 'wait' : 'pointer' }}>
              {exportando ? '⏳ Gerando…' : '📄 Exportar PDF'}
            </button>
          </div>
        </div>
        {erro ? <div style={{ color: '#dc2626', fontSize: 13 }}>⚠ {erro}</div> : !dados ? (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: 20, textAlign: 'center' }}>Cruzando a carteira com a base do INPI…</div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 340px', maxWidth: 460 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', gap: 3 }}>
                {Object.entries(UF_GRID).map(([uf, [col, lin]]) => {
                  const n = valor(uf);
                  const cor = corVerde(n, max);
                  return (
                    <div key={uf} title={`${UF_NOME[uf]}: ${n} ${rotulo} — clique para ver as marcas`}
                      onClick={() => { setUfDetalhe(uf); setBuscaMarca(''); }}
                      style={{ gridColumn: col + 1, gridRow: lin + 1, aspectRatio: '1', background: cor.bg, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer' }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: cor.texto, lineHeight: 1.1 }}>{uf}</div>
                      <div style={{ fontSize: 9.5, fontWeight: 600, color: cor.texto, opacity: 0.9 }}>{n || '·'}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 10, color: '#6b7280' }}>
                menos
                {['#dcfce7', '#86efac', '#22c55e', '#15803d', '#14532d'].map(c => <span key={c} style={{ width: 18, height: 10, background: c, borderRadius: 2, display: 'inline-block' }} />)}
                mais · clique no estado pra ver as marcas
              </div>
            </div>
            <div style={{ flex: '1 1 200px', fontSize: 12.5, color: '#374151' }}>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px', marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{modo === 'clientes' ? 'Clientes na carteira' : 'Processos na carteira'}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#15803d' }}>{modo === 'clientes' ? dados.total : dados.total_processos}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{ranking.length} estado(s) no mapa</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0a1628', marginBottom: 4 }}>RANKING</div>
              {ranking.slice(0, 10).map(([uf, n], i) => (
                <div key={uf} onClick={() => { setUfDetalhe(uf); setBuscaMarca(''); }} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <span>{i + 1}º {UF_NOME[uf]} ({uf})</span><b style={{ color: '#15803d' }}>{n}</b>
                </div>
              ))}
              {dados.sem_uf > 0 && (
                <div style={{ marginTop: 10, fontSize: 11, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '4px 8px' }}
                  title={(dados.sem_uf_nomes || []).join(' · ')}>
                  ⚠ {dados.sem_uf} cliente(s) sem UF (nem na ficha, nem na base do INPI) — passe o mouse pra ver quem
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal interno: marcas do estado clicado */}
        {ufDetalhe && (
          <div onClick={() => setUfDetalhe(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 560, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0a1628', margin: 0 }}>
                  ™ Marcas em {UF_NOME[ufDetalhe]} ({ufDetalhe}) — {marcasUf.length} processo(s)
                </h3>
                <button onClick={() => setUfDetalhe(null)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
              </div>
              <input value={buscaMarca} onChange={e => setBuscaMarca(e.target.value)} placeholder="Buscar marca, cliente ou nº do processo…"
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 8 }} />
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {marcasFiltradas.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: 13, padding: 12, textAlign: 'center' }}>Nenhuma marca {buscaMarca ? 'nessa busca' : 'neste estado'}.</div>
                ) : marcasFiltradas.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '5px 2px', borderBottom: '1px solid #f3f4f6', fontSize: 12.5 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: '#0a1628' }}>{m.marca}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>👤 {m.cliente}{m.numero ? ` · ${m.numero}` : ''}</div>
                    </div>
                    {m.cliente_id && <button onClick={() => irCliente(m.cliente_id)} style={{ ...btnSec, flexShrink: 0 }}>Ver cliente</button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const BASE = '/';
const apiFetch = (path) => fetch(`${BASE}${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}`, {
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Cache-Control': 'no-cache' },
}).then(r => r.json());
const apiSend = (path, method, body) => fetch(`${BASE}${path}`, {
  method,
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  body: body ? JSON.stringify(body) : undefined,
}).then(r => r.json());

const COR = {
  danger:  { txt: '#b91c1c', bg: '#fef2f2', bd: '#fecaca' },
  warning: { txt: '#b45309', bg: '#fffbeb', bd: '#fde68a' },
  accent:  { txt: '#1d4ed8', bg: '#eff6ff', bd: '#bfdbfe' },
  success: { txt: '#15803d', bg: '#f0fdf4', bd: '#bbf7d0' },
};

const STATUS_OPCOES = ['', 'PROTOCOLADO', 'PUBLICADO', 'EM VIGOR', 'OPOSIÇÃO', 'MANIFESTAÇÃO', 'RECURSO', 'CANCELADO', 'INDEFERIDO'];
// Cor de fundo da faixa por status
const STATUS_FAIXA = {
  '':              '#fff',
  'PROTOCOLADO':   '#fff',
  'PUBLICADO':     '#f0fdf4', // verde claro
  'EM VIGOR':      '#eff6ff', // azul claro
  'OPOSIÇÃO':      '#fffbeb', // amarelo
  'MANIFESTAÇÃO':  '#fffbeb', // amarelo
  'RECURSO':       '#fffbeb', // amarelo
  'CANCELADO':     '#fef2f2', // vermelho claro
  'INDEFERIDO':    '#fef2f2', // vermelho claro
};
// Cores sólidas para o gráfico
const STATUS_COR = {
  'PROTOCOLADO':  '#94a3b8',
  'PUBLICADO':    '#16a34a',
  'EM VIGOR':     '#2563eb',
  'OPOSIÇÃO':     '#f59e0b',
  'MANIFESTAÇÃO': '#d97706',
  'RECURSO':      '#b45309',
  'CANCELADO':    '#dc2626',
  'INDEFERIDO':   '#ef4444',
  'Sem status':   '#d1d5db',
};

function polar(cx, cy, r, ang) {
  const a = (ang - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
function arcPath(cx, cy, r, ini, fim) {
  const s = polar(cx, cy, r, fim), e = polar(cx, cy, r, ini);
  const grande = fim - ini <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${s.x} ${s.y} A ${r} ${r} 0 ${grande} 0 ${e.x} ${e.y} Z`;
}

function soDigitos(t) { return (t || '').replace(/\D/g, ''); }
function copiarTexto(texto) {
  if (!texto) return;
  const fallback = () => { const el = document.createElement('textarea'); el.value = texto; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); };
  if (navigator.clipboard) navigator.clipboard.writeText(texto).catch(fallback);
  else fallback();
}
function copiarNumero(tel) {
  let num = soDigitos(tel);
  if (!num) return;
  if (num.length <= 11) num = '55' + num;
  copiarTexto(num);
}
function waLink(tel) {
  let n = soDigitos(tel);
  if (!n) return null;
  if (n.length <= 11) n = '55' + n;
  return `https://wa.me/${n}`;
}

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14 };
const secTitulo = { fontSize: 14, fontWeight: 700, color: '#0a1628', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 };

// ── Dashboard Comparativo: até 5 procuradores lado a lado, marcos × colunas ──
const MARCOS_LINHAS = [
  { label: 'Publicação', cor: 'accent' },
  { label: 'Concessão', cor: 'success' },
  { label: 'Deferimento', cor: 'accent' },
  { label: 'Indeferimento', cor: 'danger' },
  { label: 'Oposição sofrida', cor: 'danger' },
  { label: 'Exigência', cor: 'warning' },
  { label: 'Extinção', cor: 'warning' },
  // Marcos que já geravam demanda com prazo mas não apareciam aqui
  { label: 'Exigência formal (5d)', cor: 'danger' },
  { label: 'Exigência de pagamento (5d)', cor: 'danger' },
  { label: 'Exigência de mérito', cor: 'warning' },
  { label: 'Recurso de terceiro', cor: 'danger' },
  { label: 'Nulidade instaurada', cor: 'danger' },
  { label: 'Caducidade requerida', cor: 'danger' },
  { label: 'Extinto por caducidade', cor: 'danger' },
  { label: 'Recurso provido 🎉', cor: 'success' },
];

// ── 📈 Termômetro de mercado: publicados por revista em linha 2D ──────
const CORES_LINHA = ['#0ea5e9', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899'];

function GraficoLinha({ serie, linhas, altura = 190 }) {
  // serie: [{rpi, data, ...}] cronológica · linhas: [{pega(p), cor, label}]
  const W = 760, H = altura, PAD = { t: 12, r: 14, b: 26, l: 44 };
  const n = serie.length;
  if (!n) return null;
  const max = Math.max(1, ...linhas.flatMap(l => serie.map(p => l.pega(p) || 0)));
  const x = i => PAD.l + (n === 1 ? 0 : (i / (n - 1)) * (W - PAD.l - PAD.r));
  const y = v => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);
  const gridY = [0, .25, .5, .75, 1].map(f => Math.round(max * f));
  const passoRotulo = Math.max(1, Math.ceil(n / 10));
  // Dica do mouse: o valor de cada linha na revista; com uma linha só, a
  // variação contra a revista anterior
  const rodape = linhas.length === 1
    ? (i) => (i ? variacao(`vs RPI ${serie[i - 1].rpi}`, linhas[0].pega(serie[i]) || 0, linhas[0].pega(serie[i - 1]) || 0) : null)
    : undefined;
  return (
    <DicaLinhas W={W} H={H} topo={PAD.t} base={H - PAD.b} n={n} xDe={x} yDe={y} ordenar={linhas.length > 1} largura={270}
      series={linhas.map(l => ({ nome: l.label, cor: l.cor, forte: linhas.length === 1, valor: i => l.pega(serie[i]) || 0 }))}
      titulo={i => `RPI ${serie[i].rpi}${serie[i].data ? ' · ' + serie[i].data : ''}`}
      formato={v => v.toLocaleString('pt-BR')} rodape={rodape}>
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {gridY.map((v, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={y(v)} x2={W - PAD.r} y2={y(v)} stroke="#eef1f5" strokeWidth="1" />
          <text x={PAD.l - 6} y={y(v) + 3.5} textAnchor="end" fontSize="9.5" fill="#9ca3af">{v.toLocaleString('pt-BR')}</text>
        </g>
      ))}
      {serie.map((pt, i) => i % passoRotulo === 0 && (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#6b7280">{pt.rpi}</text>
      ))}
      {linhas.map((l, li) => (
        <g key={li}>
          <polyline fill="none" stroke={l.cor} strokeWidth="2.2" strokeLinejoin="round"
            points={serie.map((pt, i) => `${x(i)},${y(l.pega(pt) || 0)}`).join(' ')} />
          {serie.map((pt, i) => (
            <circle key={i} cx={x(i)} cy={y(l.pega(pt) || 0)} r="2.6" fill={l.cor} />
          ))}
        </g>
      ))}
    </svg>
    </DicaLinhas>
  );
}

function GraficoBarras({ serie, linhas, altura = 190 }) {
  const W = 760, H = altura, PAD = { t: 12, r: 14, b: 26, l: 44 };
  const n = serie.length;
  if (!n) return null;
  const max = Math.max(1, ...linhas.flatMap(l => serie.map(p => l.pega(p) || 0)));
  const grupoW = (W - PAD.l - PAD.r) / n;
  const barW = Math.max(1.4, (grupoW * 0.78) / linhas.length);
  const y = v => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);
  const gridY = [0, .25, .5, .75, 1].map(f => Math.round(max * f));
  const passoRotulo = Math.max(1, Math.ceil(n / 10));
  // Dica do mouse: a revista sob o mouse acende e a caixa mostra cada barra
  // (do maior para o menor); com uma barra só, a variação contra a anterior
  const rodape = linhas.length === 1
    ? (i) => (i ? variacao(`vs RPI ${serie[i - 1].rpi}`, linhas[0].pega(serie[i]) || 0, linhas[0].pega(serie[i - 1]) || 0) : null)
    : undefined;
  return (
    <DicaBarras largura={270} rodape={rodape}
      titulo={i => `RPI ${serie[i].rpi}${serie[i].data ? ' · ' + serie[i].data : ''}`}
      linhas={i => linhas.map(l => ({ nome: l.label, cor: l.cor, v: l.pega(serie[i]) || 0 }))
        .sort((a, b) => b.v - a.v)
        .map(s => ({ ...s, texto: s.v.toLocaleString('pt-BR'), forte: linhas.length === 1 }))}>
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {gridY.map((v, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={y(v)} x2={W - PAD.r} y2={y(v)} stroke="#eef1f5" strokeWidth="1" />
          <text x={PAD.l - 6} y={y(v) + 3.5} textAnchor="end" fontSize="9.5" fill="#9ca3af">{v.toLocaleString('pt-BR')}</text>
        </g>
      ))}
      {serie.map((pt, i) => i % passoRotulo === 0 && (
        <text key={i} x={PAD.l + i * grupoW + grupoW / 2} y={H - 8} textAnchor="middle" fontSize="9" fill="#6b7280">{pt.rpi}</text>
      ))}
      {serie.map((pt, i) => (
        <g key={i} data-col={i}>
          {linhas.map((l, li) => {
            const v = l.pega(pt) || 0;
            return (
              <rect key={li} x={PAD.l + i * grupoW + grupoW * 0.11 + li * barW} y={y(v)}
                width={barW} height={Math.max(0, H - PAD.b - y(v))} fill={l.cor} rx="1" />
            );
          })}
        </g>
      ))}
    </svg>
    </DicaBarras>
  );
}

// Visão planilha: blocos por mês com linha de TOTAL, igual à ANÁLISE DE MERCADO
function TabelaMercado({ serie, procs }) {
  const MESES = ['JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO', 'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'];
  const curto = (n) => n.length > 14 ? n.slice(0, 12) + '…' : n;
  const grupos = [];
  for (const p of serie) {
    const dt = String(p.data || ''); // DD/MM/AAAA
    const chave = /^\d{2}\/\d{2}\/\d{4}/.test(dt) ? dt.slice(3, 10) : 'sem data';
    let g = grupos[grupos.length - 1];
    if (!g || g.chave !== chave) {
      const nome = chave === 'sem data' ? 'SEM DATA' : `${MESES[parseInt(chave.slice(0, 2), 10) - 1]} ${chave.slice(3)}`;
      g = { chave, nome, itens: [] };
      grupos.push(g);
    }
    g.itens.push(p);
  }
  const soma = (itens, pega) => itens.reduce((s, p) => s + (pega(p) || 0), 0);
  const tdB = { border: '1px solid #d7dde5', padding: '4px 8px', fontSize: 12, textAlign: 'center' };
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
      <thead>
        <tr>
          {['PERÍODO', 'RPI', 'PUBLICADOS', ...procs.map(curto)].map((h, i) => (
            <th key={i} title={i > 2 ? procs[i - 3] : ''} style={{ ...tdB, background: '#0a1628', color: '#fff', fontSize: 11, fontWeight: 700 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(() => null)()}
        {grupos.map(g => (
          <React.Fragment key={g.chave}>
            <tr><td colSpan={3 + procs.length} style={{ ...tdB, background: '#e8edf3', textAlign: 'left', fontWeight: 800, color: '#0a1628' }}>{g.nome}</td></tr>
            {g.itens.map(p => (
              <tr key={p.rpi}>
                <td style={tdB}>{p.data || '—'}</td>
                <td style={{ ...tdB, fontWeight: 700 }}>{p.rpi}</td>
                <td style={{ ...tdB, fontWeight: 700 }}>{(p.publicados || 0).toLocaleString('pt-BR')}</td>
                {procs.map(pr => <td key={pr} style={tdB}>{((p.por_proc || {})[pr] || 0).toLocaleString('pt-BR')}</td>)}
              </tr>
            ))}
            <tr>
              <td colSpan={2} style={{ ...tdB, fontWeight: 800, background: '#f8fafc', textAlign: 'right' }}>TOTAL</td>
              <td style={{ ...tdB, fontWeight: 800, background: '#f8fafc' }}>{soma(g.itens, p => p.publicados).toLocaleString('pt-BR')}</td>
              {procs.map(pr => <td key={pr} style={{ ...tdB, fontWeight: 800, background: '#f8fafc' }}>{soma(g.itens, p => (p.por_proc || {})[pr]).toLocaleString('pt-BR')}</td>)}
            </tr>
          </React.Fragment>
        ))}
        <tr>
          <td colSpan={2} style={{ ...tdB, fontWeight: 800, background: '#0a1628', color: '#fff', textAlign: 'right' }}>TOTAL DO PERÍODO</td>
          <td style={{ ...tdB, fontWeight: 800, background: '#0a1628', color: '#fff' }}>{soma(serie, p => p.publicados).toLocaleString('pt-BR')}</td>
          {procs.map(pr => <td key={pr} style={{ ...tdB, fontWeight: 800, background: '#0a1628', color: '#fff' }}>{soma(serie, p => (p.por_proc || {})[pr]).toLocaleString('pt-BR')}</td>)}
        </tr>
      </tbody>
    </table>
  );
}

function ModalMercado({ rpis, procs, onFechar }) {
  const [qtd, setQtd] = useState(26); // ~6 meses de revistas
  const [visu, setVisu] = useState('linha'); // linha | barras | planilha
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    setDados(null); setErro('');
    // Da mais antiga pra mais nova (leitura cronológica do gráfico).
    // O recorte se estende até completar o mês mais antigo — sem mês pela metade
    const todos = (rpis || []);
    const corte = todos.slice(0, qtd).slice();
    if (corte.length && corte.length < todos.length) {
      const mesMaisAntigo = String(corte[corte.length - 1].data || '').slice(3, 10);
      for (let i = corte.length; i < todos.length; i++) {
        if (String(todos[i].data || '').slice(3, 10) === mesMaisAntigo) corte.push(todos[i]);
        else break;
      }
    }
    const alvo = corte.map(r => String(r.numero)).reverse();
    if (!alvo.length) { setErro('Nenhuma revista importada.'); return; }
    apiFetch(`sucesso-cliente/mercado?rpis=${encodeURIComponent(alvo.join(','))}&procs=${encodeURIComponent(procs.join('|'))}`)
      .then(d => setDados(d)).catch(e => setErro(e?.message || 'Erro ao montar o dashboard.'));
  }, [qtd]); // eslint-disable-line

  const serie = dados?.serie || [];
  // Termômetro: média das últimas 4 revistas × média das anteriores
  let termometro = null;
  if (serie.length >= 8) {
    const vals = serie.map(p => p.publicados);
    const rec = vals.slice(-4).reduce((a, b) => a + b, 0) / 4;
    const ant = vals.slice(0, -4).reduce((a, b) => a + b, 0) / (vals.length - 4);
    const dif = ant ? Math.round((rec / ant - 1) * 100) : 0;
    termometro = { dif, quente: dif >= 5, frio: dif <= -5 };
  }
  const curto = (nome) => nome.length > 18 ? nome.slice(0, 16) + '…' : nome;

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 860, maxWidth: '96%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ background: '#0a1628', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 5 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📈 Termômetro de mercado</span>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Publicados por revista (inclui Madri) — alimentado pela importação das RPIs</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {[['linha', '📈 Linha'], ['barras', '📊 Barras'], ['planilha', '🧾 Planilha']].map(([v, l]) => (
              <button key={v} onClick={() => setVisu(v)}
                style={{ padding: '3px 10px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', background: visu === v ? '#0ea5e9' : '#132441', color: '#fff', cursor: 'pointer' }}>
                {l}
              </button>
            ))}
            <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)' }} />
            {[[13, '3 meses'], [26, '6 meses'], [52, '1 ano'], [104, '2 anos']].map(([n, l]) => (
              <button key={n} onClick={() => setQtd(n)}
                style={{ padding: '3px 10px', fontSize: 11.5, fontWeight: 700, borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', background: qtd === n ? '#fff' : '#132441', color: qtd === n ? '#0a1628' : '#fff', cursor: 'pointer' }}>
                {l}
              </button>
            ))}
            <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1, marginLeft: 4 }}>×</button>
          </div>
        </div>
        <div style={{ padding: '12px 16px' }}>
          {erro && <div style={{ color: '#dc2626', fontSize: 13, padding: 12 }}>⚠ {erro}</div>}
          {!dados && !erro && <div style={{ color: '#6b7280', fontSize: 13, padding: 20, textAlign: 'center' }}>Calculando na base local…</div>}
          {dados && serie.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#0a1628' }}>Mercado (processos publicados por revista)</span>
                {termometro && (
                  <span style={{ fontSize: 11.5, fontWeight: 800, padding: '2px 10px', borderRadius: 99,
                    background: termometro.quente ? '#dcfce7' : termometro.frio ? '#fee2e2' : '#f3f4f6',
                    color: termometro.quente ? '#15803d' : termometro.frio ? '#b91c1c' : '#374151' }}>
                    {termometro.quente ? '🔥 aquecido' : termometro.frio ? '🥶 esfriando' : '😐 estável'} · {termometro.dif > 0 ? '+' : ''}{termometro.dif}% vs média do período
                  </span>
                )}
              </div>
              {visu === 'planilha' ? (
                <div style={{ overflowX: 'auto' }}>
                  <TabelaMercado serie={[...serie].reverse()} procs={procs} />
                </div>
              ) : (
                <>
                  {visu === 'linha'
                    ? <GraficoLinha serie={serie} linhas={[{ pega: p => p.publicados, cor: '#0a1628', label: 'Publicados' }]} altura={200} />
                    : <GraficoBarras serie={serie} linhas={[{ pega: p => p.publicados, cor: '#0a1628', label: 'Publicados' }]} altura={200} />}
                  {procs.length > 0 && (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#0a1628', margin: '14px 0 4px' }}>Procuradores acompanhados</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                        {procs.map((p, i) => (
                          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: '#374151' }}>
                            <span style={{ width: 10, height: 10, borderRadius: 2, background: CORES_LINHA[i % CORES_LINHA.length], display: 'inline-block' }} />
                            {curto(p)}
                          </span>
                        ))}
                      </div>
                      {visu === 'linha'
                        ? <GraficoLinha serie={serie} altura={190}
                            linhas={procs.map((p, i) => ({ pega: pt => (pt.por_proc || {})[p] || 0, cor: CORES_LINHA[i % CORES_LINHA.length], label: p }))} />
                        : <GraficoBarras serie={serie} altura={190}
                            linhas={procs.map((p, i) => ({ pega: pt => (pt.por_proc || {})[p] || 0, cor: CORES_LINHA[i % CORES_LINHA.length], label: p }))} />}
                    </>
                  )}
                  {procs.length === 0 && (
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>Monte a lista de procuradores no Comparativo pra ver as linhas deles aqui.</div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalComparativo({ rpi, rpis, procuradores: procIniciais, inicial, onFechar }) {
  // Seleção MÚLTIPLA de revistas (bandeirinhas + lotes por mês)
  const [rpisSel, setRpisSel] = useState([String(rpi || '')].filter(Boolean));
  const [abertoRpis, setAbertoRpis] = useState(false);
  const [precisaAplicar, setPrecisaAplicar] = useState(false);
  const [listaProc, setListaProc] = useState(procIniciais || []);
  const naRpiOriginal = rpisSel.length === 1 && String(rpisSel[0]) === String(rpi || '');
  // Se o modal abrir ANTES da lista do pai terminar de carregar, sincroniza
  // quando ela chegar (só enquanto estivermos na mesma RPI da aba)
  useEffect(() => {
    if (naRpiOriginal) setListaProc(procIniciais || []);
  }, [procIniciais]); // eslint-disable-line

  // Meses disponíveis (das revistas do banco): 'MM/AAAA' → números de RPI
  const MES_NOME = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const meses = (() => {
    const m = new Map();
    for (const r of (rpis || [])) {
      const dt = String(r.data || ''); // DD/MM/AAAA
      if (!/^\d{2}\/\d{2}\/\d{4}/.test(dt)) continue;
      const chave = dt.slice(3, 10); // MM/AAAA
      if (!m.has(chave)) m.set(chave, { chave, label: `${MES_NOME[parseInt(dt.slice(3, 5), 10) - 1]}/${dt.slice(8, 10)}`, rpis: [] });
      m.get(chave).rpis.push(String(r.numero));
    }
    return [...m.values()];
  })();
  const alternarRpi = (numero) => {
    const n = String(numero);
    setRpisSel(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
    setPrecisaAplicar(true);
  };
  const alternarMes = (grupo) => {
    setRpisSel(prev => {
      const todas = grupo.rpis.every(n => prev.includes(n));
      return todas ? prev.filter(n => !grupo.rpis.includes(n)) : [...new Set([...prev, ...grupo.rpis])];
    });
    setPrecisaAplicar(true);
  };
  const recarregar = (lista) => {
    setDados({});
    buscarGeral(lista);
    // lista de busca acompanha a revista mais nova selecionada
    const maisNova = [...lista].sort((a, b) => Number(b) - Number(a))[0];
    apiFetch(`sucesso-cliente/procuradores?rpi=${encodeURIComponent(maisNova)}`)
      .then(d => setListaProc(d.procuradores || []))
      .catch(() => {});
    sels.forEach(nome => {
      buscarDados(nome, lista).then(cont => setDados(prev => ({ ...prev, [nome]: cont })));
    });
  };
  const aplicarRpis = () => {
    if (!rpisSel.length) return;
    setAbertoRpis(false);
    setPrecisaAplicar(false);
    recarregar(rpisSel);
  };
  // Setas ‹ › : navegação INDIVIDUAL revista a revista (um clique, sem
  // Aplicar) — convive com a seleção múltipla; ao navegar, vira 1 revista só
  const navegarRpi = (passo) => {
    const ordem = (rpis || []).map(r => String(r.numero)); // mais nova → mais antiga
    const atual = [...rpisSel].sort((a, b) => Number(b) - Number(a))[0] || ordem[0];
    const novo = ordem[ordem.indexOf(String(atual)) + passo];
    if (!novo) return;
    setRpisSel([novo]);
    setPrecisaAplicar(false);
    setAbertoRpis(false);
    recarregar([novo]);
  };
  const [sels, setSels] = useState([]);        // nomes escolhidos (máx 5)
  const [dados, setDados] = useState({});      // nome → { [marco]: qtd, __total }
  const [dadosGeral, setDadosGeral] = useState(null); // revista inteira (coluna Geral)
  const [dashAberto, setDashAberto] = useState(false); // 📈 termômetro de mercado
  const [busca, setBusca] = useState('');
  const normalizar = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const matches = busca.trim()
    ? listaProc.filter(p => normalizar(p.nome).includes(normalizar(busca)) && !sels.includes(p.nome)).slice(0, 8)
    : [];

  // Soma os marcos do procurador em TODAS as revistas selecionadas
  const buscarDados = async (nome, rpisAlvo) => {
    try {
      const d = await apiFetch(`sucesso-cliente/marcos-resumo?rpis=${encodeURIComponent(rpisAlvo.join(','))}&procurador=${encodeURIComponent(nome)}`);
      return { ...(d.por_marco || {}), __total: d.total || 0 };
    } catch { return { __erro: true, __total: 0 }; }
  };
  // Coluna "Geral": os marcos da(s) revista(s) inteira(s), pra comparar com a lista
  const buscarGeral = (rpisAlvo) => {
    setDadosGeral(null);
    apiFetch(`sucesso-cliente/marcos-resumo?rpis=${encodeURIComponent(rpisAlvo.join(','))}&geral=1`)
      .then(d => setDadosGeral({ ...(d.por_marco || {}), __total: d.total || 0 }))
      .catch(() => setDadosGeral({ __total: 0 }));
  };

  const adicionar = async (nome, rpisAlvo = rpisSel) => {
    if (!nome || sels.includes(nome) || sels.length >= 5) return;
    setSels(s => [...s, nome]);
    setBusca('');
    const cont = await buscarDados(nome, rpisAlvo);
    setDados(prev => ({ ...prev, [nome]: cont }));
  };

  // Abertura: semeia com a lista SALVA (a sequência que o Vagner acompanha)
  // + o procurador selecionado na aba (se houver) e busca o geral da revista
  useEffect(() => {
    let salvos = [];
    try { salvos = JSON.parse(localStorage.getItem('comparativoProcs') || '[]'); } catch { salvos = []; }
    const fila = [...new Set([...(inicial ? [inicial] : []), ...salvos])].slice(0, 5);
    fila.forEach(n => adicionar(n));
    buscarGeral(rpisSel);
  }, []); // eslint-disable-line
  // Toda mudança na lista fica salva pras próximas pesquisas
  const jaCarregou = React.useRef(false);
  useEffect(() => {
    if (!jaCarregou.current) { jaCarregou.current = true; return; }
    localStorage.setItem('comparativoProcs', JSON.stringify(sels));
  }, [sels]);

  const remover = (nome) => {
    setSels(s => s.filter(n => n !== nome));
    setDados(prev => { const { [nome]: _, ...resto } = prev; return resto; });
  };
  const curto = (nome) => nome.length > 22 ? nome.slice(0, 20) + '…' : nome;

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      {dashAberto && <ModalMercado rpis={rpis} procs={sels} onFechar={() => setDashAberto(false)} />}
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 820, maxWidth: '96%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ background: '#0a1628', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>🆚 Comparativo de procuradores</span>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Até 5 procuradores · marcos × quantidade de processos</div>
            </div>
            <button onClick={() => navegarRpi(1)} title="Revista anterior (mais antiga) — modo individual"
              style={{ padding: '4px 9px', fontSize: 13, fontWeight: 700, borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', background: '#132441', color: '#fff', cursor: 'pointer' }}>‹</button>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setAbertoRpis(a => !a)}
                title="Selecionar uma ou várias revistas (com lote por mês)"
                style={{ padding: '4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', background: '#132441', color: '#fff', cursor: 'pointer' }}>
                📅 {rpisSel.length === 1
                  ? `RPI ${rpisSel[0]}${(() => { const d = (rpis || []).find(r => String(r.numero) === String(rpisSel[0]))?.data; return d ? ' · ' + d : ''; })()}`
                  : `${rpisSel.length} revistas`} ▾
              </button>
              {abertoRpis && (
                <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 60, background: '#fff', border: '1px solid #c8d0da', borderRadius: 8, width: 340, maxWidth: '86vw', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
                  {/* Lotes por mês: um clique marca/desmarca todas as revistas do mês */}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    {meses.map(g => {
                      const todas = g.rpis.every(n => rpisSel.includes(n));
                      return (
                        <button key={g.chave} onClick={() => alternarMes(g)}
                          title={`${g.rpis.length} revista(s) — clique pra ${todas ? 'desmarcar' : 'marcar'} o mês inteiro`}
                          style={{ padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 99, cursor: 'pointer', border: '1px solid ' + (todas ? '#0a1628' : '#c8d0da'), background: todas ? '#0a1628' : '#fff', color: todas ? '#fff' : '#374151' }}>
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {(rpis || []).map(r => {
                      const marcada = rpisSel.includes(String(r.numero));
                      return (
                        <div key={r.numero} onClick={() => alternarRpi(r.numero)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f9fafb', background: marcada ? '#f0f9ff' : '#fff', color: '#0a1628' }}>
                          <span style={{ width: 16, height: 16, borderRadius: 4, border: '2px solid ' + (marcada ? '#16a34a' : '#c8d0da'), background: marcada ? '#16a34a' : '#fff', color: '#fff', fontSize: 11, fontWeight: 800, lineHeight: '13px', textAlign: 'center', flexShrink: 0 }}>{marcada ? '✓' : ''}</span>
                          <span style={{ fontWeight: marcada ? 700 : 400 }}>RPI {r.numero}{r.data ? ` — ${r.data}` : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderTop: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{rpisSel.length} selecionada(s)</span>
                    <button onClick={aplicarRpis} disabled={!rpisSel.length}
                      style={{ padding: '4px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: 'none', background: rpisSel.length ? '#16a34a' : '#9ca3af', color: '#fff', cursor: rpisSel.length ? 'pointer' : 'default' }}>
                      ✓ Aplicar
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => navegarRpi(-1)} title="Próxima revista (mais nova) — modo individual"
              style={{ padding: '4px 9px', fontSize: 13, fontWeight: 700, borderRadius: 6, border: '1px solid rgba(255,255,255,0.35)', background: '#132441', color: '#fff', cursor: 'pointer' }}>›</button>
            {precisaAplicar && <span style={{ fontSize: 11, color: '#fbbf24', fontWeight: 700 }}>← toque Aplicar pra recalcular</span>}
            <button onClick={() => setDashAberto(true)} title="Publicados por revista em gráfico de linha — o termômetro de sazonalidade do mercado"
              style={{ padding: '4px 12px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: 'none', background: '#0ea5e9', color: '#fff', cursor: 'pointer' }}>
              📈 Dashboard
            </button>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #e8eaed' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: sels.length ? 8 : 0 }}>
            {sels.map(nome => (
              <span key={nome} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#0a1628', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 99, padding: '3px 10px' }}>
                {curto(nome)}
                <button onClick={() => remover(nome)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            ))}
          </div>
          {sels.length < 5 && (
            <div style={{ position: 'relative' }}>
              <input value={busca} onChange={e => setBusca(e.target.value)}
                placeholder={`Digite o nome do ${sels.length ? 'próximo ' : ''}procurador… (${5 - sels.length} vaga${5 - sels.length > 1 ? 's' : ''})`}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, border: '1px solid #b0b8c4', fontSize: 13, outline: 'none' }} />
              {matches.length > 0 && (
                <div style={{ position: 'absolute', top: '105%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #c8d0da', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                  {matches.map(p => (
                    <div key={p.nome} onClick={() => adicionar(p.nome)}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nome}</span>
                      <span style={{ color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{p.qtd}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1, padding: '12px 14px' }}>
          {sels.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>Adicione procuradores acima pra montar a tabela.</div>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, color: '#fff', background: '#0a1628', borderRadius: '6px 0 0 0' }}>Marco</th>
                  <th title="A revista inteira, todos os procuradores" style={{ textAlign: 'center', padding: '6px 8px', fontSize: 11, color: '#cbd5e1', background: '#132441' }}>🌐 Geral da revista</th>
                  {sels.map(nome => (
                    <th key={nome} title={nome} style={{ textAlign: 'center', padding: '6px 8px', fontSize: 11, color: '#fff', background: '#0a1628' }}>{curto(nome)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MARCOS_LINHAS.map(l => {
                  const c = COR[l.cor] || COR.accent;
                  const valores = sels.map(nome => dados[nome] ? (dados[nome][l.label] || 0) : null);
                  const max = Math.max(...valores.map(v => v || 0));
                  return (
                    <tr key={l.label}>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #f3f4f6' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: c.txt, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 5, padding: '2px 8px', whiteSpace: 'nowrap' }}>{l.label}</span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '5px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700, color: '#475569', background: '#f8fafc' }}>
                        {dadosGeral === null ? '…' : (dadosGeral[l.label] || 0)}
                      </td>
                      {valores.map((v, i) => (
                        <td key={i} style={{ textAlign: 'center', padding: '5px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: v !== null && v === max && max > 0 ? 800 : 500, color: v !== null && v === max && max > 0 ? '#0a1628' : '#6b7280', background: v !== null && v === max && max > 0 ? '#f0fdf4' : 'transparent' }}>
                          {v === null ? '…' : v}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ padding: '7px 10px', fontSize: 12, fontWeight: 800, color: '#0a1628', borderTop: '2px solid #0a1628' }}>TOTAL</td>
                  <td style={{ textAlign: 'center', padding: '7px 8px', fontSize: 13.5, fontWeight: 800, color: '#475569', background: '#f8fafc', borderTop: '2px solid #0a1628' }}>
                    {dadosGeral === null ? '…' : dadosGeral.__total}
                  </td>
                  {sels.map(nome => (
                    <td key={nome} style={{ textAlign: 'center', padding: '7px 8px', fontSize: 13.5, fontWeight: 800, color: '#0a1628', borderTop: '2px solid #0a1628' }}>
                      {dados[nome] ? dados[nome].__total : '…'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SucessoCliente() {
  const navigate = useNavigate();
  const [resumo, setResumo] = useState(null);
  const [renovacoes, setRenovacoes] = useState(null);
  const [marcos, setMarcos] = useState(null);
  const [rpis, setRpis] = useState([]);
  const [rpiSel, setRpiSel] = useState('');
  const [carregandoMarcos, setCarregandoMarcos] = useState(false);
  const [baixandoInpi, setBaixandoInpi] = useState(null); // numero_processo em download
  const [gerandoCert, setGerandoCert] = useState(null); // numero_processo do certificado em geração
  // Download em LOTE das páginas do INPI: flags nos cards → um PDF único
  const [selInpi, setSelInpi] = useState(new Set());
  const [loteProg, setLoteProg] = useState('');
  const [modalDemandaLote, setModalDemandaLote] = useState(false);
  const toggleSelInpi = (numero) => setSelInpi(prev => {
    const s = new Set(prev);
    if (s.has(numero)) s.delete(numero); else s.add(numero);
    return s;
  });
  const [modalNovaDemanda, setModalNovaDemanda] = useState(null); // clienteInfo pro ModalDemanda

  // 📅 Nova tarefa da Agenda direto do card do marco (cliente já preenchido)
  const [modalNovaTarefa, setModalNovaTarefa] = useState(null); // {cliente_id, cliente_nome, descricao}
const SUBAGENDAS_OP = [['', '📥 Geral'], ['administrativo', '🗂️ Administrativo'], ['comercial', '💼 Comercial'], ['cliente', '👥 Cliente'], ['demandas', '⚖️ Demandas Processuais'], ['pessoal', '🏠 Pessoal']];
  const [tarefaForm, setTarefaForm] = useState({ descricao: '', data_agendada: '', destinado_a: '', urgente: false, subagenda: 'cliente' });
  const [salvandoTarefa, setSalvandoTarefa] = useState(false);
  const abrirNovaTarefa = (m) => {
    setTarefaForm({
      descricao: `${m.marco}: ${m.marca || m.numero_processo} — ${m.acao}`,
      data_agendada: '',
      destinado_a: localStorage.getItem('nomeUsuario') || '',
      urgente: false,
    });
    setModalNovaTarefa({ cliente_id: m.cliente_id, cliente_nome: m.cliente || '' });
  };
  const salvarNovaTarefa = async () => {
    if (!tarefaForm.descricao.trim()) return alert('Descreva a tarefa.');
    setSalvandoTarefa(true);
    try {
      await apiSend('agenda', 'POST', {
        ...tarefaForm,
        cliente_id: modalNovaTarefa.cliente_id,
        cliente_nome: modalNovaTarefa.cliente_nome,
      });
      setModalNovaTarefa(null);
    } catch (e) {
      alert('Erro ao criar a tarefa: ' + (e?.message || ''));
    } finally {
      setSalvandoTarefa(false);
    }
  };

  // Baixa a página oficial do processo no site do INPI em PDF (leva ~20-40s:
  // o servidor loga no pePI, abre a página e imprime). Arquivo com o nome
  // da marca; sem marca, cai pro número do processo.
  // Certificado de deferimento/concessão em PDF (paisagem, com logo e
  // assinatura) — pra mandar ao cliente junto com a boa notícia
  async function gerarCertificado(m) {
    if (gerandoCert) return;
    const chave = m.numero_processo || m.marca;
    setGerandoCert(chave);
    try {
      const dataRpi = (rpis.find(r => String(r.numero) === String(marcos?.rpi))?.data) || '';
      const dados = {
        evento: m.tipo === 'concessao' ? 'concessao' : 'deferimento',
        marca: m.marca || '', processo: m.numero_processo || '',
        titular: m.cliente || '', rpi: marcos?.rpi || '', data_rpi: dataRpi,
      };
      const r = await fetch('/documentos/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ tipo: 'certificado', dados }),
      });
      if (!r.ok) {
        let det = ''; try { det = (await r.json()).erro || ''; } catch { /* não-JSON */ }
        return alert(`Não consegui gerar o certificado (HTTP ${r.status}). ${det}`.trim());
      }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      const nomeLimpo = String(m.marca || '').trim().replace(/[\\/:*?"<>|]/g, '');
      a.href = url; a.download = `Certificado ${nomeLimpo || m.numero_processo}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Falha ao gerar o certificado: ' + (e?.message || 'erro de rede'));
    } finally {
      setGerandoCert(null);
    }
  }

  async function baixarPaginaInpi(numero, marca) {
    if (baixandoInpi) return;
    setBaixandoInpi(numero);
    try {
      const r = await fetch(`/inpi/pagina-pdf/${encodeURIComponent(numero)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!r.ok) {
        let det = ''; try { det = (await r.json()).erro || ''; } catch { /* não-JSON */ }
        return alert(`Não consegui baixar a página do INPI (HTTP ${r.status}). ${det}`.trim());
      }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      const nomeLimpo = String(marca || '').trim().replace(/[\\/:*?"<>|]/g, '');
      a.href = url; a.download = nomeLimpo ? `${nomeLimpo}.pdf` : `INPI ${numero}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Falha ao baixar a página do INPI: ' + (e?.message || 'erro de rede'));
    } finally {
      setBaixandoInpi(null);
    }
  }

  // Baixa as páginas selecionadas como ARQUIVOS SEPARADOS (um PDF por
  // processo, nome da marca + número). Sequencial com RESPIRO e até 2
  // tentativas por processo — o pePI engasga em rajada, e um deploy no meio
  // do lote (caso 11/08) não derruba mais tudo.
  async function baixarLoteInpi() {
    const numeros = [...selInpi];
    if (!numeros.length || loteProg) return;
    const marcaDe = new Map();
    for (const m of (marcos?.marcos || [])) {
      if (m.numero_processo && !marcaDe.has(m.numero_processo)) marcaDe.set(m.numero_processo, m.marca || '');
    }
    const falhas = [];
    let ok = 0;
    for (let i = 0; i < numeros.length; i++) {
      const numero = numeros[i];
      let blob = null, erroMsg = '';
      for (let tent = 1; tent <= 2 && !blob; tent++) {
        setLoteProg(`${i + 1}/${numeros.length} — ${numero}${tent > 1 ? ' (2ª tentativa)' : ''}…`);
        try {
          const r = await fetch(`/inpi/pagina-pdf/${encodeURIComponent(numero)}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          blob = await r.blob();
        } catch (e) {
          erroMsg = e?.message || 'erro';
          if (tent < 2) await new Promise(rs => setTimeout(rs, 4000));
        }
      }
      if (blob) {
        const nomeLimpo = String(marcaDe.get(numero) || '').trim().replace(/[\\/:*?"<>|]/g, '');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        // número sempre no nome: marcas repetidas (3 processos da mesma) não colidem
        a.download = nomeLimpo ? `${nomeLimpo} ${numero}.pdf` : `INPI ${numero}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        ok += 1;
      } else falhas.push(`${numero} (${erroMsg})`);
      await new Promise(rs => setTimeout(rs, 1500));
    }
    setLoteProg('');
    if (ok) setSelInpi(new Set());
    alert(`🏛️ ${ok} arquivo(s) baixado(s).` + (falhas.length ? `\n\nFalharam: ${falhas.join(', ')}\nSeleciona só esses e roda de novo.` : ''));
  }
  const [contatos, setContatos] = useState(null);
  const [buscaContato, setBuscaContato] = useState('');
  const [filtroContato, setFiltroContato] = useState('todos');
  // Visão do ciclo: agrupado por cliente ou 1 linha por processo (recente 1º)
  const [visaoCiclo, setVisaoCicloState] = useState(localStorage.getItem('sucessoVisaoCiclo') || 'cliente');
  const setVisaoCiclo = (v) => { setVisaoCicloState(v); localStorage.setItem('sucessoVisaoCiclo', v); };
  const [filtroStatus, setFiltroStatus] = useState('');
  const [paginaContato, setPaginaContato] = useState(1);
  const [salvandoContato, setSalvandoContato] = useState(null);
  const [mostrarDashboard, setMostrarDashboard] = useState(false);
  const [mostrarExportar, setMostrarExportar] = useState(false);
  const [baixando, setBaixando] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState('marcos');
  const [msgTpls, setMsgTpls] = useState([]);
  const [modalWhats, setModalWhats] = useState(null);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [modalLote, setModalLote] = useState(false);
  const [modalRelatorio, setModalRelatorio] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [r, ren, mar] = await Promise.all([
        apiFetch('sucesso-cliente/resumo'),
        apiFetch('sucesso-cliente/renovacoes'),
        apiFetch('sucesso-cliente/marcos'),
      ]);
      setResumo(r); setRenovacoes(ren); setMarcos(mar);
      if (mar?.rpi) setRpiSel(String(mar.rpi));
    } catch { /* silencioso */ }
    setCarregando(false);
  }, []);

  const carregarRpis = useCallback(async () => {
    try { const d = await apiFetch('sucesso-cliente/rpis'); setRpis(d.rpis || []); } catch { /* silencioso */ }
  }, []);

  // Filtros da aba Marcos: procurador (vazio = MEUS clientes, o padrão) e
  // tipo de movimentação-marco
  const [procSel, setProcSel] = useState('');
  const [tipoSel, setTipoSel] = useState('');
  const [procuradores, setProcuradores] = useState([]);
  const [modalDashUf, setModalDashUf] = useState(false); // ranking de UFs dos marcos filtrados
  // Dashboard Quantitativo: processos por MARCO do procurador selecionado —
  // ignora o filtro de tipo (busca os dados completos por conta própria)
  const [modalDashQtd, setModalDashQtd] = useState(false);
  const [modalComparativo, setModalComparativo] = useState(false); // 🆚 até 5 procuradores
  const [dashQtd, setDashQtd] = useState(null);
  const abrirDashQtd = async () => {
    setModalDashQtd(true);
    setDashQtd(null);
    try {
      const q = [rpiSel ? `rpi=${rpiSel}` : '', procSel ? `procurador=${encodeURIComponent(procSel)}` : ''].filter(Boolean).join('&');
      setDashQtd(await apiFetch(`sucesso-cliente/marcos${q ? `?${q}` : ''}`));
    } catch { setDashQtd({ erro: 'Falha ao buscar os dados' }); }
  };
  // Busca digitável de procurador (lista completa da revista)
  const [buscaProc, setBuscaProc] = useState('');
  const [abertoProc, setAbertoProc] = useState(false);
  const procRef = useRef(null);
  useEffect(() => {
    const fora = (e) => { if (procRef.current && !procRef.current.contains(e.target)) setAbertoProc(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);
  const normalizarTxt = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const procMatches = buscaProc.trim()
    ? procuradores.filter(p => normalizarTxt(p.nome).includes(normalizarTxt(buscaProc))).slice(0, 50)
    : procuradores.slice(0, 30);
  const escolherProc = (nome) => {
    setProcSel(nome);
    setAbertoProc(false);
    setBuscaProc('');
    carregarMarcos(rpiSel, nome, tipoSel);
  };

  const carregarMarcos = useCallback(async (rpi, proc = '', tipo = '') => {
    setCarregandoMarcos(true);
    try {
      const q = [
        rpi ? `rpi=${rpi}` : '',
        proc ? `procurador=${encodeURIComponent(proc)}` : '',
        tipo ? `tipo=${encodeURIComponent(tipo)}` : '',
      ].filter(Boolean).join('&');
      const mar = await apiFetch(`sucesso-cliente/marcos${q ? `?${q}` : ''}`);
      setMarcos(mar);
      if (mar?.rpi) setRpiSel(String(mar.rpi));
    } catch { /* silencioso */ }
    setCarregandoMarcos(false);
  }, []);

  // Top procuradores da RPI selecionada (alimenta o filtro)
  useEffect(() => {
    apiFetch(`sucesso-cliente/procuradores${rpiSel ? `?rpi=${rpiSel}` : ''}`)
      .then(d => setProcuradores(d.procuradores || []))
      .catch(() => {});
  }, [rpiSel]);

  const carregarContatos = useCallback(async () => {
    try { setContatos(await apiFetch('sucesso-cliente/contatos')); } catch { /* silencioso */ }
  }, []);

  const carregarMsgs = useCallback(async () => {
    try { const d = await apiFetch('sucesso-cliente/mensagens'); setMsgTpls(Array.isArray(d) ? d : []); } catch { /* silencioso */ }
  }, []);

  useEffect(() => { carregar(); carregarRpis(); carregarMsgs(); }, [carregar, carregarRpis, carregarMsgs]);
  useEffect(() => { if (aba === 'contatos') carregarContatos(); }, [aba, carregarContatos]);

  // numero opcional: marca o contato DAQUELE processo (sem número = cliente
  // inteiro, cobre todos os processos existentes hoje)
  const marcarContato = async (id, numero) => {
    setSalvandoContato(id);
    await apiSend('sucesso-cliente/contatos', 'POST', { cliente_id: id, processo_numero: numero || '' });
    await carregarContatos();
    setSalvandoContato(null);
  };
  const desfazerContato = async (id, numero) => {
    setSalvandoContato(id);
    await apiSend(`sucesso-cliente/contatos/${id}${numero ? `?processo=${numero}` : ''}`, 'DELETE');
    await carregarContatos();
    setSalvandoContato(null);
  };
  const baixarExport = async (formato) => {
    setBaixando(formato);
    try {
      const resp = await fetch(`${BASE}sucesso-cliente/contatos/export/${formato}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!resp.ok) throw new Error('falha');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = formato === 'excel' ? 'contatos_ciclo.xlsx' : 'contatos_ciclo.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { alert('Não foi possível gerar o arquivo. Tente novamente.'); }
    setBaixando('');
  };
  // Seletor de status nos cards de Marcos: grava o MESMO status do ciclo
  // (coleção status_cliente) — a aba Contatos do ciclo reflete na hora,
  // porque recarrega toda vez que é aberta
  const mudarStatusMarco = async (clienteId, status) => {
    setMarcos(prev => prev ? { ...prev, marcos: prev.marcos.map(x => x.cliente_id === clienteId ? { ...x, status } : x) } : prev);
    setContatos(prev => prev ? { ...prev, itens: prev.itens.map(it => it.cliente_id === clienteId ? { ...it, status } : it) } : prev);
    await apiSend('sucesso-cliente/status', 'POST', { cliente_id: clienteId, status });
  };

  // numero opcional: muda o status SÓ daquele processo (sem numero = todos)
  const mudarStatus = async (id, status, numero) => {
    // Atualiza local na hora (a faixa muda de cor imediatamente)
    setContatos(prev => prev ? { ...prev, itens: prev.itens.map(it => {
      if (it.cliente_id !== id) return it;
      const mps2 = (it.marcas_processos || []).map(mp =>
        (!numero || mp.numero === numero) ? { ...mp, status } : mp);
      const vals = [...new Set(mps2.map(mp => mp.status || ''))];
      return { ...it, marcas_processos: mps2, status: vals.length === 1 ? vals[0] : '', status_misto: vals.length > 1 };
    }) } : prev);
    await apiSend('sucesso-cliente/status', 'POST', { cliente_id: id, status, processo_numero: numero || '' });
  };

  const irCliente = (id) => id && navigate(`/clientes/${id}/editar`);
  const toggleSel = (id) => setSelecionados(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const Metric = ({ label, valor, destaque }) => (
    <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: destaque || '#0a1628', marginTop: 2 }}>{valor}</div>
    </div>
  );

  if (carregando) return (
    <div style={{ maxWidth: 1000, padding: 20, textAlign: 'center', color: '#6b7280' }}>Carregando...</div>
  );

  return (
    <div style={{ maxWidth: 1000, padding: 16 }}>
      <h1 style={{ fontSize: 19, fontWeight: 700, color: '#0a1628', margin: '0 0 4px' }}>💚 Sucesso do Cliente</h1>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
        Relacionamento pós-venda, marcos da RPI e renovações da carteira.
      </p>

      {modalNovaDemanda && <ModalDemanda clienteInfo={modalNovaDemanda}
        onFechar={() => setModalNovaDemanda(null)} onSalvo={() => setModalNovaDemanda(null)} />}
      {modalDemandaLote && <ModalDemandaLote
        marcos={marcos} selInpi={selInpi} rpis={rpis}
        onFechar={() => setModalDemandaLote(false)}
        onSalvo={() => { setModalDemandaLote(false); setSelInpi(new Set()); }} />}

      {modalNovaTarefa && (
        <div onClick={() => !salvandoTarefa && setModalNovaTarefa(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 440, width: '100%', padding: 16 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', margin: '0 0 2px' }}>📅 Nova tarefa na Agenda</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>Cliente: <strong style={{ color: '#0a1628' }}>{modalNovaTarefa.cliente_nome}</strong></div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Descrição</label>
            <textarea value={tarefaForm.descricao} onChange={e => setTarefaForm(f => ({ ...f, descricao: e.target.value }))} rows={3}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 8, resize: 'vertical' }} />
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Data/hora (opcional)</label>
            <div style={{ marginBottom: 8 }}><CampoDataHora value={tarefaForm.data_agendada} onChange={v => setTarefaForm(f => ({ ...f, data_agendada: v }))} style={{ width: '100%' }} /></div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Destinado a</label>
            <SelectColaborador value={tarefaForm.destinado_a} onChange={v => setTarefaForm(f => ({ ...f, destinado_a: v }))} placeholder="Selecione..." />
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginTop: 8 }}>Subagenda</label>
            <select value={tarefaForm.subagenda || 'cliente'} onChange={e => setTarefaForm(f => ({ ...f, subagenda: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
              {SUBAGENDAS_OP.map(([v, l]) => <option key={v || 'g'} value={v}>{l}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#374151', margin: '10px 0' }}>
              <input type="checkbox" checked={tarefaForm.urgente} onChange={e => setTarefaForm(f => ({ ...f, urgente: e.target.checked }))} /> Urgente
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModalNovaTarefa(null)} disabled={salvandoTarefa}
                style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={salvarNovaTarefa} disabled={salvandoTarefa}
                style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {salvandoTarefa ? 'Salvando…' : 'Criar tarefa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Métricas */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <Metric label="Clientes na carteira" valor={resumo?.total_clientes ?? '—'} />
        <Metric label="Processos na carteira" valor={resumo?.total_processos ?? '—'} destaque="#7c3aed" />
        <Metric label="Marcos nesta RPI" valor={marcos?.total ?? '—'} destaque="#1d4ed8" />
        <Metric label="Renovações em 12m" valor={resumo?.renovacoes?.em_12m ?? '—'} />
        <Metric label="Renovações em 3m" valor={resumo?.renovacoes?.em_3m ?? '—'} destaque="#b45309" />
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { k: 'marcos', label: `🔔 Marcos da RPI ${marcos?.rpi ? `(${marcos.rpi})` : ''}` },
          { k: 'renovacoes', label: '🔄 Renovações' },
          { k: 'contatos', label: '☎️ Contatos do ciclo' },
          { k: 'mensagens', label: '✍️ Gerador de mensagem' },
          { k: 'mapa', label: '🗺️ Mapa da carteira' },
        ].map(t => (
          <button key={t.k} onClick={() => setAba(t.k)} style={{
            padding: '7px 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
            border: '1px solid ' + (aba === t.k ? '#0a1628' : '#d1d5db'),
            background: aba === t.k ? '#0a1628' : '#fff', color: aba === t.k ? '#fff' : '#374151',
          }}>{t.label}</button>
        ))}
      </div>

      {/* MARCOS DA RPI */}
      {aba === 'marcos' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#0a1628' }}>Selecione a RPI:</label>
            <select
              value={rpiSel}
              onChange={e => carregarMarcos(e.target.value, procSel, tipoSel)}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #0a1628', background: '#fff', color: '#0a1628', cursor: 'pointer' }}>
              {rpis.map(r => (
                <option key={r.numero} value={r.numero}>RPI {r.numero}{r.data ? ` — ${r.data}` : ''} ({r.marcas} marcas)</option>
              ))}
            </select>
            {/* Padrão = MEUS clientes; a busca digitável cobre TODOS os
                procuradores da revista (digite pra filtrar) */}
            <div ref={procRef} style={{ position: 'relative' }}>
              <button onClick={() => setAbertoProc(a => !a)}
                style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: `1px solid ${procSel ? '#b45309' : '#0a1628'}`, background: procSel ? '#fffbeb' : '#fff', color: procSel ? '#b45309' : '#0a1628', cursor: 'pointer', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {procSel || '👥 Meus clientes (padrão)'} ▾
              </button>
              {abertoProc && (
                <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50, background: '#fff', border: '1px solid #c8d0da', borderRadius: 8, width: 360, maxWidth: '90vw', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                  <input autoFocus value={buscaProc} onChange={e => setBuscaProc(e.target.value)}
                    placeholder={`Digite o nome… (${procuradores.length.toLocaleString('pt-BR')} procuradores na revista)`}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: 'none', borderBottom: '1px solid #e5e7eb', fontSize: 13, outline: 'none' }} />
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    <div onClick={() => escolherProc('')}
                      style={{ padding: '7px 10px', fontSize: 12.5, fontWeight: 700, color: '#0a1628', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', background: !procSel ? '#f0f9ff' : '#fff' }}>
                      👥 Meus clientes (padrão)
                    </div>
                    {procMatches.map(p => (
                      <div key={p.nome} onClick={() => escolherProc(p.nome)}
                        style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #f9fafb', background: procSel === p.nome ? '#fffbeb' : '#fff' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = procSel === p.nome ? '#fffbeb' : '#fff'}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nome}</span>
                        <span style={{ color: '#6b7280', fontWeight: 700, flexShrink: 0 }}>{p.qtd}</span>
                      </div>
                    ))}
                    {procMatches.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>Nenhum procurador com esse nome nesta revista.</div>}
                  </div>
                  <div style={{ padding: '5px 10px', fontSize: 10.5, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
                    {buscaProc.trim() ? `${procMatches.length} resultado(s)` : `mostrando os ${procMatches.length} maiores`} de {procuradores.length.toLocaleString('pt-BR')} — digite pra refinar
                  </div>
                </div>
              )}
            </div>
            <select
              value={tipoSel}
              onChange={e => { setTipoSel(e.target.value); carregarMarcos(rpiSel, procSel, e.target.value); }}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid #0a1628', background: '#fff', color: '#0a1628', cursor: 'pointer' }}>
              <option value="">Todos os marcos</option>
              <option value="concessao">🎉 Concessão</option>
              <option value="publicacao">📣 Publicação</option>
              <option value="deferimento">✅ Deferimento</option>
              <option value="indeferimento">⛔ Indeferimento</option>
              <option value="oposicao">⚔️ Oposição sofrida</option>
              <option value="exigencia">⚠️ Exigência</option>
              <option value="extincao">⌛ Extinção</option>
            </select>
            <button onClick={() => setModalDashUf(true)} title="Ranking das UFs por volume, respeitando os filtros atuais"
              style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: '#0a1628', color: '#fff', cursor: 'pointer' }}>
              📊 Dashboard UF
            </button>
            <button onClick={abrirDashQtd} title="Quantidade de processos por marco do procurador selecionado — ignora o filtro de tipo"
              style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', cursor: 'pointer' }}>
              📈 Quantitativo
            </button>
            <button onClick={() => setModalComparativo(true)} title="Compare até 5 procuradores: marcos × quantidade de processos"
              style={{ padding: '6px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: '#6d28d9', color: '#fff', cursor: 'pointer' }}>
              🆚 Comparativo
            </button>
            {carregandoMarcos && <span style={{ fontSize: 12, color: '#6b7280' }}>carregando...</span>}
          </div>
          {(!marcos?.marcos || marcos.marcos.length === 0) ? (
            <div style={{ ...card, textAlign: 'center', color: '#6b7280', padding: 30 }}>
              {procSel
                ? `Nenhuma movimentação-marco de "${procSel}" na RPI ${marcos?.rpi || 'atual'}${tipoSel ? ' nesse tipo' : ''}.`
                : `Nenhum cliente com movimentação na RPI ${marcos?.rpi || 'atual'}${tipoSel ? ' nesse tipo' : ''}.`}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span>{marcos.marcos.length} movimentação(ões) {procSel ? <>do procurador <strong style={{ color: '#b45309' }}>{procSel}</strong></> : 'em processos de clientes'} na RPI {marcos.rpi} — ordenadas por urgência.</span>
                <button onClick={() => setSelInpi(new Set(marcos.marcos.map(x => x.numero_processo).filter(Boolean)))}
                  style={{ background: 'none', border: 'none', color: '#0a1628', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>☑ marcar todos</button>
                {selInpi.size > 0 && (
                  <>
                    <button onClick={() => setSelInpi(new Set())}
                      style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>✕ desmarcar todos</button>
                    <button onClick={baixarLoteInpi} disabled={!!loteProg}
                      title="Baixa a página oficial de cada processo selecionado — um arquivo PDF por processo, com o nome da marca"
                      style={{ background: loteProg ? '#e5e7eb' : '#0a1628', color: loteProg ? '#6b7280' : '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: loteProg ? 'wait' : 'pointer' }}>
                      {loteProg ? `⏳ ${loteProg}` : `🏛️ Baixar páginas juntas (${selInpi.size})`}
                    </button>
                    <button onClick={() => setModalDemandaLote(true)}
                      title="Cria a MESMA demanda (tipo + prazo) pra todos os processos selecionados de uma vez"
                      style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      ⚖️ + Demanda em lote ({selInpi.size})
                    </button>
                  </>
                )}
              </div>
              {marcos.marcos.map((m, i) => {
                const c = COR[m.cor] || COR.accent;
                const link = waLink(m.telefone);
                return (
                  <div key={i} style={{ ...card, borderLeft: `4px solid ${c.txt}`, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {m.numero_processo && (
                            <input type="checkbox" checked={selInpi.has(m.numero_processo)} onChange={() => toggleSelInpi(m.numero_processo)}
                              title="Selecionar pro download em lote da página do INPI"
                              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#0a1628', flexShrink: 0 }} />
                          )}
                          <span style={{ fontSize: 11, fontWeight: 700, color: c.txt, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 5, padding: '2px 8px' }}>{m.marco}</span>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#0a1628' }}>{m.marca || '(sem marca)'}</span>
                          <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{m.numero_processo}</span>
                          {m.uf && (
                            <span title={`Titular de ${m.uf}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 800, color: '#0a1628', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 5, padding: '1px 7px' }}>
                              <img src={`/bandeiras/${String(m.uf).toLowerCase()}.png`} alt="" width={16} height={11}
                                style={{ objectFit: 'cover', borderRadius: 2, border: '1px solid rgba(0,0,0,0.15)' }}
                                onError={e => { e.currentTarget.style.display = 'none'; }} />
                              {m.uf}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
                          👤 {m.cliente} · <span style={{ color: c.txt }}>{m.acao}</span>
                        </div>
                      </div>
                      {/* Controles no canto: três pilhas de 2 */}
                      <div style={{ display: 'grid', gridAutoFlow: 'column', gridTemplateRows: 'repeat(2, auto)', gap: 6, alignSelf: 'flex-start', marginLeft: 'auto' }}>
                        {m.cliente_id && (
                          <select
                            value={m.status || ''}
                            onChange={e => mudarStatusMarco(m.cliente_id, e.target.value)}
                            title="Status do processo — o mesmo da aba Contatos do ciclo"
                            style={{ fontSize: 11, fontWeight: 600, padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', width: 128 }}>
                            {STATUS_OPCOES.map(s => <option key={s} value={s}>{s || '— status —'}</option>)}
                          </select>
                        )}
                        {m.numero_processo && (
                          <button onClick={() => baixarPaginaInpi(m.numero_processo, m.marca)} disabled={!!baixandoInpi}
                            title="Baixa a página oficial deste processo no site do INPI em PDF (demora ~30s: o servidor entra no pePI e imprime a página)"
                            style={{ ...btnSec, background: baixandoInpi === m.numero_processo ? '#e5e7eb' : '#0a1628', color: baixandoInpi === m.numero_processo ? '#6b7280' : '#fff', cursor: baixandoInpi ? 'wait' : 'pointer', width: 128 }}>
                            {baixandoInpi === m.numero_processo ? '⏳ Baixando…' : '🏛️ Página INPI'}
                          </button>
                        )}
                        {['deferimento', 'concessao', 'recurso_provido'].includes(m.tipo) && (
                          <button onClick={() => gerarCertificado(m)} disabled={!!gerandoCert}
                            title="Gera o certificado em PDF (deferimento ou concessão, conforme o marco) pra enviar ao cliente"
                            style={{ ...btnSec, background: gerandoCert === (m.numero_processo || m.marca) ? '#e5e7eb' : '#b45309', color: gerandoCert === (m.numero_processo || m.marca) ? '#6b7280' : '#fff', cursor: gerandoCert ? 'wait' : 'pointer', width: 128 }}>
                            {gerandoCert === (m.numero_processo || m.marca) ? '⏳ Gerando…' : '📜 Certificado'}
                          </button>
                        )}
                        {m.cliente_id && (
                          <button onClick={() => abrirNovaTarefa(m)}
                            title="Criar tarefa na Agenda com este cliente já preenchido"
                            style={{ ...btnSec, background: '#16a34a', color: '#fff', width: 128 }}>
                            📅 + Tarefa
                          </button>
                        )}
                        {m.cliente_id && (
                          <button onClick={() => setModalNovaDemanda({ cliente_id: m.cliente_id, cliente_nome: m.cliente || '', processo_numero: m.numero_processo || '', marca: m.marca || '', data_publicacao: (rpis.find(r => String(r.numero) === String(marcos.rpi))?.data) || '' })}
                            title="Lançar demanda processual (oposição, recurso, exigência…) com prazo"
                            style={{ ...btnSec, background: '#7c3aed', color: '#fff', width: 128 }}>
                            ⚖️ + Demanda
                          </button>
                        )}
                        {m.cliente_id && <button onClick={() => irCliente(m.cliente_id)} style={{ ...btnSec, width: 128 }}>Ver cliente</button>}
                        {link && <a href={link} target="_blank" rel="noreferrer" style={{ ...btnWa, textDecoration: 'none', width: 128, textAlign: 'center', boxSizing: 'border-box', display: 'inline-block' }}>WhatsApp</a>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* RENOVAÇÕES */}
      {aba === 'renovacoes' && (
        <div>
          {(!renovacoes?.itens || renovacoes.itens.length === 0) ? (
            <div style={{ ...card, textAlign: 'center', color: '#6b7280', padding: 30 }}>
              <div style={{ fontSize: 15, marginBottom: 6 }}>Nenhuma renovação nos próximos 12 meses. 👍</div>
              <div style={{ fontSize: 12 }}>A carteira é nova — as marcas registradas vencem a partir de 2030.<br />Os alertas aparecerão aqui automaticamente conforme os decênios se aproximam.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['3m', '6m', '12m'].map(bucket => {
                const itens = renovacoes.itens.filter(i => i.bucket === bucket);
                if (!itens.length) return null;
                const rot = { '3m': '⏰ Vence em até 3 meses', '6m': '📅 Vence em 3 a 6 meses', '12m': '🗓️ Vence em 6 a 12 meses' }[bucket];
                const corB = { '3m': '#b45309', '6m': '#1d4ed8', '12m': '#6b7280' }[bucket];
                return (
                  <div key={bucket} style={card}>
                    <div style={{ ...secTitulo, color: corB }}>{rot} ({itens.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {itens.map((r, i) => {
                        const link = waLink(r.telefone);
                        return (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < itens.length - 1 ? '1px solid #f3f4f6' : 'none', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13 }}>
                              <b>{r.marca || '(sem marca)'}</b> — {r.cliente}
                              <span style={{ color: r.vencida ? '#b91c1c' : '#6b7280', marginLeft: 6 }}>
                                {r.vencida ? 'venceu' : 'vence'} {r.vencimento}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => irCliente(r.cliente_id)} style={btnSec}>Ver cliente</button>
                              {link && <a href={link} target="_blank" rel="noreferrer" style={{ ...btnWa, textDecoration: 'none' }}>WhatsApp</a>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CONTATOS DO CICLO */}
      {aba === 'contatos' && (
        <div>
          {!contatos ? (
            <div style={{ ...card, textAlign: 'center', color: '#6b7280', padding: 30 }}>Carregando clientes...</div>
          ) : (() => {
            const termo = buscaContato.trim().toLowerCase();
            const filtrados = contatos.itens.filter(c => {
              if (filtroContato === 'contatados' && c.dias_sem_contato === null) return false;
              if (filtroContato === 'nao' && c.dias_sem_contato !== null) return false;
              if (filtroStatus === '__sem__' && (c.status || '')) return false;
              if (filtroStatus && filtroStatus !== '__sem__' && (c.status || '') !== filtroStatus) return false;
              if (!termo) return true;
              const termoDigitos = soDigitos(termo);
              return c.cliente.toLowerCase().includes(termo)
                || (c.marca || '').toLowerCase().includes(termo)
                || (termoDigitos.length > 0 && soDigitos(c.telefone).includes(termoDigitos))
                || (termoDigitos.length > 0 && (c.marcas_processos || []).some(mp => soDigitos(mp.numero).includes(termoDigitos)));
            });
            // Linhas da lista: agrupado (1 linha por cliente, processos empilhados)
            // ou por processo (1 linha por processo, nº mais recente primeiro)
            const explodir = (c) => (c.marcas_processos && c.marcas_processos.length) ? c.marcas_processos : [{ marca: c.marca || '', numero: '' }];
            const linhas = visaoCiclo === 'cliente'
              ? filtrados.map(c => ({ c, mps: explodir(c) }))
              : filtrados
                  .flatMap(c => explodir(c).map(mp => ({ c, mps: [mp] })))
                  .sort((a, b) => Number(b.mps[0].numero || 0) - Number(a.mps[0].numero || 0));
            return (
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  <input
                    value={buscaContato}
                    onChange={e => { setBuscaContato(e.target.value); setPaginaContato(1); }}
                    placeholder="Buscar por nome, marca, telefone ou nº do processo..."
                    style={{ flex: 1, minWidth: 220, padding: '8px 12px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 8, outline: 'none' }}
                  />
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {visaoCiclo === 'processo' ? `${linhas.length} processos` : `${filtrados.length} de ${contatos.total}`}
                  </span>
                  <button onClick={() => setMostrarDashboard(true)} style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    📊 Dashboard
                  </button>
                  <button onClick={() => setMostrarExportar(true)} style={{ background: '#fff', color: '#0a1628', border: '1px solid #0a1628', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    ⬇️ Exportar
                  </button>
                  <button onClick={() => setModalRelatorio(true)} style={{ background: '#fff', color: '#0a1628', border: '1px solid #0a1628', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    📊 Relatório de envios
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Liga/desliga da visão: agrupado por cliente ⇄ por processo */}
                  <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 7, overflow: 'hidden' }}>
                    {[['cliente', '👥 Por cliente'], ['processo', '🧾 Por processo']].map(([v, rot]) => (
                      <button key={v} onClick={() => { setVisaoCiclo(v); setPaginaContato(1); }} style={{
                        padding: '5px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                        background: visaoCiclo === v ? '#0a1628' : '#fff', color: visaoCiclo === v ? '#fff' : '#374151',
                      }}>{rot}</button>
                    ))}
                  </div>
                  {[
                    { k: 'todos', label: `Todos (${contatos.total})` },
                    { k: 'nao', label: `Não contatados (${contatos.nunca_contatados})` },
                    { k: 'contatados', label: `Já contatados (${contatos.total - contatos.nunca_contatados})` },
                  ].map(f => (
                    <button key={f.k} onClick={() => { setFiltroContato(f.k); setPaginaContato(1); }} style={{
                      padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer',
                      border: '1px solid ' + (filtroContato === f.k ? '#0a1628' : '#d1d5db'),
                      background: filtroContato === f.k ? '#0a1628' : '#fff', color: filtroContato === f.k ? '#fff' : '#374151',
                    }}>{f.label}</button>
                  ))}
                  <select
                    value={filtroStatus}
                    onChange={e => { setFiltroStatus(e.target.value); setPaginaContato(1); }}
                    title="Filtrar por status"
                    style={{ padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', border: '1px solid ' + (filtroStatus ? '#0a1628' : '#d1d5db'), background: filtroStatus ? '#0a1628' : '#fff', color: filtroStatus ? '#fff' : '#374151' }}>
                    <option value="">Status: todos</option>
                    <option value="__sem__">Sem status</option>
                    {STATUS_OPCOES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {msgTpls.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap', background: selecionados.size > 0 ? '#f0fdf4' : 'transparent', border: '1px solid ' + (selecionados.size > 0 ? '#bbf7d0' : 'transparent'), borderRadius: 8, padding: '6px 10px' }}>
                    <label style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox"
                        checked={filtrados.length > 0 && filtrados.every(c => selecionados.has(c.cliente_id))}
                        onChange={e => setSelecionados(e.target.checked ? new Set(filtrados.map(c => c.cliente_id)) : new Set())} />
                      Selecionar todos ({filtrados.length}) p/ envio em lote
                    </label>
                    {selecionados.size > 0 && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{selecionados.size} selecionado(s)</span>}
                    {selecionados.size > 0 && <button onClick={() => setSelecionados(new Set())} style={{ ...btnSec, padding: '4px 10px' }}>Limpar</button>}
                    <div style={{ flex: 1 }} />
                    {selecionados.size > 0 && (
                      <button onClick={() => setModalLote(true)} style={{ background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ✉️ Enviar em lote ({selecionados.size})
                      </button>
                    )}
                  </div>
                )}
                <div style={{ ...card, padding: 0, maxHeight: 560, overflowY: 'auto' }}>
                  {filtrados.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>Nenhum cliente encontrado.</div>
                  ) : linhas.slice((paginaContato - 1) * 100, paginaContato * 100).map(({ c, mps }, i) => {
                    const link = waLink(c.telefone);
                    const nunca = c.dias_sem_contato === null;
                    const salvando = salvandoContato === c.cliente_id;
                    // Linha de UM processo (modo por-processo): status próprio
                    const rowNumero = mps.length === 1 ? (mps[0].numero || '') : '';
                    const rowStatus = rowNumero ? (mps[0].status || '') : (c.status || '');
                    return (
                      <div key={c.cliente_id + '|' + (mps[0]?.numero || i)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: i < linhas.length - 1 ? '1px solid #f3f4f6' : 'none', background: selecionados.has(c.cliente_id) ? '#ecfdf5' : (STATUS_FAIXA[rowStatus] || '#fff') }}>
                        {msgTpls.length > 0 && <input type="checkbox" checked={selecionados.has(c.cliente_id)} onChange={() => toggleSel(c.cliente_id)} title="Selecionar para envio em lote" style={{ flexShrink: 0, width: 16, height: 16, cursor: 'pointer' }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13 }}>
                            {(mps.length > 0 && mps.some(mp => mp.marca || mp.numero))
                              ? mps.map((mp, k) => (
                                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, marginTop: k > 0 ? 3 : 0, flexWrap: 'wrap' }}>
                                    <span title={c.cliente} style={{ width: 160, flexShrink: 0, fontWeight: 600, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {k === 0 ? c.cliente : ''}
                                    </span>
                                    <span title={mp.marca || ''} style={{ flex: 1, minWidth: 110, maxWidth: 260, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {mp.marca || '(sem marca)'}
                                    </span>
                                    {mp.numero ? (
                                      <>
                                        <span
                                          onClick={() => irCliente(c.cliente_id)}
                                          title="Abrir cadastro do cliente"
                                          style={{ color: '#1d4ed8', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'monospace', flexShrink: 0 }}>
                                          {mp.numero}
                                        </span>
                                        <button
                                          onClick={e => { e.stopPropagation(); copiarTexto(mp.numero); }}
                                          title="Copiar número do processo"
                                          style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, padding: '0 4px', fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>
                                          📋
                                        </button>
                                        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                                          {mp.dias_sem_contato === null ? (
                                            <>
                                              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '2px 8px', whiteSpace: 'nowrap' }}>nunca contatado</span>
                                              <button disabled={salvando} onClick={e => { e.stopPropagation(); marcarContato(c.cliente_id, mp.numero); }}
                                                title="Marcar contato SÓ deste processo"
                                                style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '2px 9px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer' }}>✓ contatei</button>
                                            </>
                                          ) : (
                                            <>
                                              <span title={mp.ultimo_por ? `por ${mp.ultimo_por}` : ''} style={{ fontSize: 10.5, fontWeight: 700, color: mp.dias_sem_contato > 90 ? '#b45309' : '#15803d', background: mp.dias_sem_contato > 90 ? '#fffbeb' : '#f0fdf4', border: '1px solid ' + (mp.dias_sem_contato > 90 ? '#fde68a' : '#bbf7d0'), borderRadius: 8, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                                                ✓ há {mp.dias_sem_contato === 0 ? 'hoje' : `${mp.dias_sem_contato}d`}
                                              </span>
                                              <button disabled={salvando} onClick={e => { e.stopPropagation(); marcarContato(c.cliente_id, mp.numero); }}
                                                title="Registrar novo contato deste processo"
                                                style={{ background: '#fff', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 7px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer' }}>✓</button>
                                              {mp.especifico && (
                                                <button disabled={salvando} onClick={e => { e.stopPropagation(); desfazerContato(c.cliente_id, mp.numero); }}
                                                  title="Desfazer o contato deste processo"
                                                  style={{ background: '#fff', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 6px', fontSize: 10.5, cursor: 'pointer' }}>↺</button>
                                              )}
                                            </>
                                          )}
                                        </span>
                                      </>
                                    ) : <span style={{ color: '#9ca3af', flexShrink: 0 }}>—</span>}
                                  </div>
                                ))
                              : (
                                <div style={{ fontWeight: 600, color: '#0a1628', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {c.cliente}{c.marca ? <span style={{ fontWeight: 400, color: '#6b7280' }}> — {c.marca}</span> : ''}
                                </div>
                              )}
                          </div>
                          <div style={{ fontSize: 11, marginTop: 1 }}>
                            {nunca
                              ? <span style={{ color: '#b45309' }}>Nunca contatado</span>
                              : <span style={{ color: c.dias_sem_contato > 90 ? '#b45309' : '#16a34a' }}>
                                  Último contato há {c.dias_sem_contato === 0 ? 'hoje' : `${c.dias_sem_contato}d`}{c.ultimo_por ? ` · ${c.ultimo_por}` : ''}
                                </span>}
                          </div>
                        </div>
                        <select
                          value={rowStatus}
                          onChange={e => mudarStatus(c.cliente_id, e.target.value, rowNumero || undefined)}
                          title={rowNumero ? `Status SÓ do processo ${rowNumero}` : 'Status de TODOS os processos deste cliente'}
                          style={{ fontSize: 11, fontWeight: 600, padding: '4px 6px', borderRadius: 6, border: '1px solid ' + (c.status_misto && !rowNumero ? '#f59e0b' : '#d1d5db'), background: '#fff', color: '#374151', cursor: 'pointer', maxWidth: 130 }}>
                          {c.status_misto && !rowNumero && <option value="">— vários —</option>}
                          {STATUS_OPCOES.filter(sv => sv || !(c.status_misto && !rowNumero)).map(sv => <option key={sv} value={sv}>{sv || '— status —'}</option>)}
                        </select>
                        {link && <button onClick={() => setModalWhats(c)} style={btnWa} title="Enviar WhatsApp">WhatsApp</button>}
                        {c.telefone && <button onClick={() => copiarNumero(c.telefone)} title="Copiar número" style={btnCopiar}>📋</button>}
                        {nunca ? (
                          <button disabled={salvando} onClick={() => marcarContato(c.cliente_id)} title="Marca contato em TODOS os processos atuais do cliente" style={{ ...btnMarcar, opacity: salvando ? 0.6 : 1 }}>
                            {salvando ? '...' : '✓ Marcar contato'}
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: 5 }}>
                            <button disabled={salvando} onClick={() => marcarContato(c.cliente_id)} title="Marca contato em TODOS os processos atuais do cliente" style={{ ...btnMarcar, opacity: salvando ? 0.6 : 1 }}>
                              {salvando ? '...' : '✓ Contatei'}
                            </button>
                            <button disabled={salvando} onClick={() => desfazerContato(c.cliente_id)} title="Desfazer último contato" style={btnSec}>↺</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {linhas.length > 100 && (() => {
                  const totalPaginas = Math.ceil(linhas.length / 100);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, marginTop: 10, flexWrap: 'wrap' }}>
                      <button disabled={paginaContato === 1} onClick={() => setPaginaContato(p => p - 1)}
                        style={{ ...btnPag, opacity: paginaContato === 1 ? 0.4 : 1, cursor: paginaContato === 1 ? 'default' : 'pointer' }}>‹</button>
                      {Array.from({ length: totalPaginas }, (_, k) => k + 1).map(n => (
                        <button key={n} onClick={() => setPaginaContato(n)} style={{
                          ...btnPag,
                          background: paginaContato === n ? '#0a1628' : '#fff',
                          color: paginaContato === n ? '#fff' : '#374151',
                          border: '1px solid ' + (paginaContato === n ? '#0a1628' : '#d1d5db'),
                        }}>{n}</button>
                      ))}
                      <button disabled={paginaContato === totalPaginas} onClick={() => setPaginaContato(p => p + 1)}
                        style={{ ...btnPag, opacity: paginaContato === totalPaginas ? 0.4 : 1, cursor: paginaContato === totalPaginas ? 'default' : 'pointer' }}>›</button>
                      <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 6 }}>
                        {(paginaContato - 1) * 100 + 1}–{Math.min(paginaContato * 100, linhas.length)} de {linhas.length}
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      )}

      {aba === 'mensagens' && (
        <AbaMensagens templates={msgTpls} recarregar={carregarMsgs} />
      )}

      {aba === 'mapa' && <AbaMapaCarteira />}

      {mostrarDashboard && contatos && (
        <DashboardStatus itens={contatos.itens} onFechar={() => setMostrarDashboard(false)} />
      )}

      {mostrarExportar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !baixando && setMostrarExportar(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⬇️</div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0a1628', marginBottom: 8 }}>Exportar listagem</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
              Baixe a lista completa de {contatos?.total ?? ''} clientes com marcas, telefone, status e último contato.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => baixarExport('pdf')} disabled={!!baixando}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 14, fontWeight: 600, cursor: baixando ? 'default' : 'pointer', opacity: baixando ? 0.6 : 1 }}>
                {baixando === 'pdf' ? 'Gerando...' : '📄 PDF'}
              </button>
              <button onClick={() => baixarExport('excel')} disabled={!!baixando}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 14, fontWeight: 600, cursor: baixando ? 'default' : 'pointer', opacity: baixando ? 0.6 : 1 }}>
                {baixando === 'excel' ? 'Gerando...' : '📊 Excel'}
              </button>
            </div>
            <button onClick={() => !baixando && setMostrarExportar(false)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, marginTop: 18, cursor: 'pointer' }}>Fechar</button>
          </div>
        </div>
      )}

      {modalWhats && (
        <ModalWhats contato={modalWhats} templates={msgTpls} onFechar={() => setModalWhats(null)} />
      )}

      {modalDashUf && (() => {
        // Ranking de UFs a partir dos marcos JÁ filtrados (RPI + procurador + tipo)
        const itens = marcos?.marcos || [];
        const porUf = new Map();
        for (const m of itens) {
          const uf = (m.uf || '').trim().toUpperCase() || '(sem UF)';
          porUf.set(uf, (porUf.get(uf) || 0) + 1);
        }
        const ranking = [...porUf.entries()].sort((a, b) => b[1] - a[1]);
        const max = ranking[0]?.[1] || 1;
        const TIPO_LABEL = { concessao: 'Concessão', publicacao: 'Publicação', deferimento: 'Deferimento', indeferimento: 'Indeferimento', oposicao: 'Oposição', exigencia: 'Exigência', extincao: 'Extinção',
          exigencia_formal: 'Exigência formal (5d)', exigencia_pagto: 'Exigência de pagamento (5d)', exigencia_merito: 'Exigência de mérito',
          recurso_terceiro: 'Recurso de terceiro', nulidade: 'Nulidade instaurada', caducidade: 'Caducidade requerida',
          extinto_caducidade: 'Extinto por caducidade', recurso_provido: 'Recurso provido 🎉', arquivamento: 'Arquivamento definitivo' };
        return (
          <div onClick={() => setModalDashUf(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 460, maxWidth: '94%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
              <div style={{ background: '#0a1628', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📊 Ranking das UFs — RPI {marcos?.rpi || ''}</span>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    {procSel || 'Meus clientes'} · {tipoSel ? TIPO_LABEL[tipoSel] : 'todos os marcos'} · {itens.length} movimentação(ões)
                  </div>
                </div>
                <button onClick={() => setModalDashUf(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1, padding: '12px 14px' }}>
                {ranking.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Nenhuma movimentação nos filtros atuais.</div>
                ) : ranking.map(([uf, qtd], i) => (
                  <div key={uf} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', width: 24, textAlign: 'right' }}>{i + 1}º</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#0a1628', width: 64 }}>{uf}</span>
                    <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 99, height: 14, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.max(4, Math.round(qtd / max * 100))}%`, background: i === 0 ? '#0a1628' : '#60a5fa', height: '100%' }} />
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0a1628', width: 36, textAlign: 'right' }}>{qtd}</span>
                    <span style={{ fontSize: 11, color: '#6b7280', width: 34, textAlign: 'right' }}>{Math.round(qtd / itens.length * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
      {modalComparativo && (
        <ModalComparativo rpi={rpiSel} rpis={rpis} procuradores={procuradores} inicial={procSel}
          onFechar={() => setModalComparativo(false)} />
      )}
      {modalDashQtd && (() => {
        const itens = dashQtd?.marcos || [];
        const porMarco = new Map();
        for (const m of itens) {
          const g = porMarco.get(m.marco) || { marco: m.marco, cor: m.cor, qtd: 0 };
          g.qtd += 1;
          porMarco.set(m.marco, g);
        }
        const ranking = [...porMarco.values()].sort((a, b) => b.qtd - a.qtd);
        const max = ranking[0]?.qtd || 1;
        return (
          <div onClick={() => setModalDashQtd(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 460, maxWidth: '94%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
              <div style={{ background: '#1d4ed8', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📈 Quantitativo por marco — RPI {dashQtd?.rpi || rpiSel}</span>
                  <div style={{ fontSize: 11, color: '#bfdbfe', marginTop: 1 }}>
                    {procSel || 'Meus clientes'} · todos os tipos · {itens.length} movimentação(ões)
                  </div>
                </div>
                <button onClick={() => setModalDashQtd(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1, padding: '12px 14px' }}>
                {!dashQtd ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 20, fontSize: 13 }}>Calculando…</div>
                ) : dashQtd.erro ? (
                  <div style={{ textAlign: 'center', color: '#dc2626', padding: 20, fontSize: 13 }}>⚠ {String(dashQtd.erro)}</div>
                ) : ranking.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>Nenhuma movimentação-marco {procSel ? 'desse procurador' : 'da carteira'} nesta RPI.</div>
                ) : ranking.map((g, i) => {
                  const c = COR[g.cor] || COR.accent;
                  return (
                    <div key={g.marco} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: c.txt, background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 5, padding: '2px 8px', width: 118, textAlign: 'center', flexShrink: 0 }}>{g.marco}</span>
                      <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 99, height: 14, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(4, Math.round(g.qtd / max * 100))}%`, background: c.txt, height: '100%' }} />
                      </div>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0a1628', width: 40, textAlign: 'right' }}>{g.qtd}</span>
                      <span style={{ fontSize: 11, color: '#6b7280', width: 34, textAlign: 'right' }}>{Math.round(g.qtd / itens.length * 100)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      {modalLote && (
        <ModalLote
          lista={(contatos?.itens || []).filter(c => selecionados.has(c.cliente_id))}
          templates={msgTpls}
          onFechar={() => { setModalLote(false); carregarContatos(); }}
        />
      )}

      {modalRelatorio && (
        <ModalRelatorio templates={msgTpls} onFechar={() => setModalRelatorio(false)} />
      )}
    </div>
  );
}

function DashboardStatus({ itens, onFechar }) {
  const ordem = ['PROTOCOLADO', 'PUBLICADO', 'EM VIGOR', 'OPOSIÇÃO', 'MANIFESTAÇÃO', 'RECURSO', 'CANCELADO', 'INDEFERIDO', 'Sem status'];
  const cont = {};
  itens.forEach(c => { const s = c.status || 'Sem status'; cont[s] = (cont[s] || 0) + 1; });
  const dados = ordem.filter(s => cont[s]).map(s => ({ status: s, qtd: cont[s] }));
  const total = itens.length;

  let anguloAtual = 0;
  const fatias = dados.map(d => {
    const frac = d.qtd / total;
    const ini = anguloAtual;
    const fim = anguloAtual + frac * 360;
    anguloAtual = fim;
    return { ...d, ini, fim, pct: Math.round(frac * 100), cor: STATUS_COR[d.status] || '#d1d5db' };
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 560, width: '92%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0a1628', margin: 0 }}>📊 Distribuição por status</h2>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        {dados.length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: 20 }}>Nenhum cliente para exibir.</p>
        ) : (
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
            <svg viewBox="0 0 200 200" style={{ width: 200, height: 200, flexShrink: 0 }}>
              {fatias.length === 1
                ? <circle cx="100" cy="100" r="90" fill={fatias[0].cor} />
                : fatias.map((f, i) => <path key={i} d={arcPath(100, 100, 90, f.ini, f.fim)} fill={f.cor} stroke="#fff" strokeWidth="1" />)}
            </svg>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 200 }}>
              {fatias.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <span style={{ width: 14, height: 14, borderRadius: 3, background: f.cor, flexShrink: 0 }} />
                  <span style={{ flex: 1, color: '#374151' }}>{f.status}</span>
                  <span style={{ fontWeight: 700, color: '#0a1628' }}>{f.pct}%</span>
                  <span style={{ color: '#9ca3af', fontSize: 12, minWidth: 34, textAlign: 'right' }}>({f.qtd})</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#0a1628' }}>
                <span>Total</span><span>{total}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Gerador de Mensagem ───────────────────────────────
const VARIAVEIS_MSG = ['[NOME]', '[MARCA]', '[PROCESSO]'];
const lblMsg = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 };
const inpMsg = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const codeTag = { background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '0 4px', fontSize: 11, fontFamily: 'monospace' };

// [NOME]/[CLIENTE] usa só o PRIMEIRO nome, com apenas a inicial maiúscula
// (ex.: "JOÃO DA SILVA" -> "João", "maria" -> "Maria")
function primeiroNome(nome) {
  const p = (nome || '').trim().split(/\s+/)[0] || '';
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : '';
}

// Troca [NOME]/[CLIENTE], [MARCA], [PROCESSO]/[NUMERO] pelos dados do cliente
function adaptarMsg(texto, { nome = '', marca = '', processo = '' }) {
  const pn = primeiroNome(nome);
  return (texto || '')
    .replace(/\[NOME\]/gi, pn)
    .replace(/\[CLIENTE\]/gi, pn)
    .replace(/\[MARCA\]/gi, marca)
    .replace(/\[PROCESSO\]/gi, processo)
    .replace(/\[NUMERO\]/gi, processo);
}

function AbaMensagens({ templates, recarregar }) {
  const [form, setForm] = useState({ titulo: '', texto: '' });
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!form.titulo.trim() || !form.texto.trim()) return alert('Preencha título e texto.');
    setSalvando(true);
    if (editando) await apiSend(`sucesso-cliente/mensagens/${editando}`, 'PUT', form);
    else await apiSend('sucesso-cliente/mensagens', 'POST', form);
    setSalvando(false);
    setForm({ titulo: '', texto: '' }); setEditando(null); recarregar();
  };
  const excluir = async (id) => {
    if (!window.confirm('Excluir esta mensagem?')) return;
    await apiSend(`sucesso-cliente/mensagens/${id}`, 'DELETE');
    if (editando === id) { setEditando(null); setForm({ titulo: '', texto: '' }); }
    recarregar();
  };
  const editar = (t) => { setEditando(t._id); setForm({ titulo: t.titulo, texto: t.texto }); window.scrollTo(0, 0); };
  const inserir = (v) => setForm(f => ({ ...f, texto: (f.texto ? f.texto + ' ' : '') + v }));
  const preview = adaptarMsg(form.texto, { nome: 'JOÃO DA SILVA', marca: 'MINHA MARCA', processo: '912345678' });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
      <div style={card}>
        <div style={secTitulo}>{editando ? '✏️ Editar mensagem' : '➕ Nova mensagem'}</div>
        <label style={lblMsg}>Título</label>
        <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex.: Marca publicada" style={inpMsg} />
        <label style={{ ...lblMsg, marginTop: 10 }}>Texto da mensagem</label>
        <textarea value={form.texto} onChange={e => setForm(f => ({ ...f, texto: e.target.value }))} placeholder="Ex.: Olá [NOME]! Sua marca [MARCA] (processo [PROCESSO]) teve movimentação na RPI." style={{ ...inpMsg, minHeight: 110, resize: 'vertical' }} />
        <div style={{ fontSize: 11, color: '#6b7280', margin: '8px 0 5px' }}>Clique para inserir:</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {VARIAVEIS_MSG.map(v => (
            <button key={v} onClick={() => inserir(v)} style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 5, padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>{v}</button>
          ))}
        </div>
        {form.texto && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Pré-visualização (exemplo):</div>
            <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{preview}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={salvar} disabled={salvando} style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>{salvando ? 'Salvando...' : editando ? 'Atualizar' : 'Salvar mensagem'}</button>
          {editando && <button onClick={() => { setEditando(null); setForm({ titulo: '', texto: '' }); }} style={btnSec}>Cancelar</button>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>Use <code style={codeTag}>[NOME]</code>, <code style={codeTag}>[MARCA]</code> e <code style={codeTag}>[PROCESSO]</code> — o sistema troca pelos dados do cliente na hora de enviar. O <code style={codeTag}>[NOME]</code> usa só o primeiro nome, com a inicial maiúscula (ex.: “João”).</div>
        {(!templates || templates.length === 0) ? (
          <div style={{ ...card, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nenhuma mensagem cadastrada ainda.</div>
        ) : templates.map(t => (
          <div key={t._id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628' }}>{t.titulo}</div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => editar(t)} style={{ ...btnSec, padding: '3px 10px', fontSize: 11 }}>Editar</button>
                <button onClick={() => excluir(t._id)} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Excluir</button>
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'pre-wrap', lineHeight: 1.5, marginTop: 6 }}>{t.texto}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModalWhats({ contato, templates, onFechar }) {
  const mps = (contato.marcas_processos && contato.marcas_processos.length)
    ? contato.marcas_processos
    : [{ marca: contato.marca || '', numero: contato.numero || '' }];
  const [idxMp, setIdxMp] = useState(0);
  const [tplId, setTplId] = useState('');
  const mp = mps[idxMp] || mps[0] || {};
  const link = waLink(contato.telefone);
  const tpl = (templates || []).find(t => t._id === tplId) || null;
  const textoAdaptado = tpl ? adaptarMsg(tpl.texto, { nome: contato.cliente, marca: mp.marca, processo: mp.numero }) : '';

  const registrarEnvio = (payload) => apiSend('sucesso-cliente/envios', 'POST', {
    cliente_id: contato.cliente_id, cliente_nome: contato.cliente, telefone: contato.telefone, ...payload,
  }).catch(() => {});
  const abrirDireto = () => {
    if (link) window.open(link, '_blank', 'noopener');
    registrarEnvio({ template_titulo: 'Sem mensagem', texto: '', origem: 'direto' });
    onFechar();
  };
  const abrirComMsg = () => {
    if (!tpl || !link) return;
    window.open(`${link}?text=${encodeURIComponent(textoAdaptado)}`, '_blank', 'noopener');
    registrarEnvio({ template_id: tpl._id, template_titulo: tpl.titulo, texto: textoAdaptado, marca: mp.marca, numero_processo: mp.numero, origem: 'individual' });
    onFechar();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: 460, maxWidth: '94%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>💬 WhatsApp</h2>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 14 }}>{contato.cliente} · <span style={{ color: '#6b7280' }}>{contato.telefone}</span></div>

        <button onClick={abrirDireto} style={{ width: '100%', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>
          Abrir conversa direto (sem mensagem)
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px' }}>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>OU USE UMA MENSAGEM PROGRAMADA</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        </div>

        {(!templates || templates.length === 0) ? (
          <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '8px 0 4px' }}>Nenhuma mensagem cadastrada. Crie na aba “Gerador de mensagem”.</div>
        ) : (
          <>
            {mps.length > 1 && (
              <div style={{ marginBottom: 10 }}>
                <label style={lblMsg}>Marca / processo</label>
                <select value={idxMp} onChange={e => setIdxMp(Number(e.target.value))} style={inpMsg}>
                  {mps.map((m, i) => <option key={i} value={i}>{(m.marca || '(sem marca)')}{m.numero ? ` — ${m.numero}` : ''}</option>)}
                </select>
              </div>
            )}
            <label style={lblMsg}>Escolha a mensagem</label>
            <select value={tplId} onChange={e => setTplId(e.target.value)} style={inpMsg}>
              <option value="">— selecione —</option>
              {templates.map(t => <option key={t._id} value={t._id}>{t.titulo}</option>)}
            </select>

            {tpl && (
              <div style={{ background: '#e9fbe9', border: '1px solid #bbf7d0', borderRadius: 12, borderBottomRightRadius: 3, padding: '11px 13px', fontSize: 14, lineHeight: 1.55, color: '#1a1a1a', whiteSpace: 'pre-wrap', margin: '12px 0' }}>
                {textoAdaptado}
              </div>
            )}

            <button onClick={abrirComMsg} disabled={!tpl} style={{ width: '100%', background: tpl ? '#25d366' : '#a7f3c0', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 700, cursor: tpl ? 'pointer' : 'default', marginTop: 4 }}>
              Abrir WhatsApp com a mensagem pronta
            </button>
            <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 8 }}>Abre a conversa com o texto preenchido. Você confere e clica em enviar.</div>
          </>
        )}
      </div>
    </div>
  );
}

function ModalLote({ lista, templates, onFechar }) {
  const [tplId, setTplId] = useState('');
  const [enviados, setEnviados] = useState(() => new Set());
  const [registrar, setRegistrar] = useState(true);
  const tpl = (templates || []).find(t => t._id === tplId) || null;

  // No lote, usa a 1ª marca/processo de cada cliente
  const mpDe = (c) => (c.marcas_processos && c.marcas_processos.length) ? c.marcas_processos[0] : { marca: c.marca || '', numero: c.numero || '' };
  const textoDe = (c) => tpl ? adaptarMsg(tpl.texto, { nome: c.cliente, marca: mpDe(c).marca, processo: mpDe(c).numero }) : '';

  const comTel = (lista || []).filter(c => waLink(c.telefone));
  const semTel = (lista || []).length - comTel.length;
  const pendentes = comTel.filter(c => !enviados.has(c.cliente_id));
  const proximo = pendentes[0] || null;
  const pct = comTel.length ? Math.round((enviados.size / comTel.length) * 100) : 0;

  const enviar = (c) => {
    const link = waLink(c.telefone);
    if (!link || !tpl) return;
    window.open(`${link}?text=${encodeURIComponent(textoDe(c))}`, '_blank', 'noopener');
    setEnviados(prev => new Set(prev).add(c.cliente_id));
    apiSend('sucesso-cliente/envios', 'POST', {
      cliente_id: c.cliente_id, cliente_nome: c.cliente, telefone: c.telefone,
      template_id: tpl._id, template_titulo: tpl.titulo, texto: textoDe(c),
      marca: mpDe(c).marca, numero_processo: mpDe(c).numero, origem: 'lote',
    }).catch(() => {});
    if (registrar) apiSend('sucesso-cliente/contatos', 'POST', { cliente_id: c.cliente_id }).catch(() => {});
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 540, maxWidth: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px', borderBottom: '1px solid #f3f4f6' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>✉️ Envio em lote · {(lista || []).length} clientes</h2>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
          <label style={lblMsg}>Mensagem</label>
          <select value={tplId} onChange={e => setTplId(e.target.value)} style={inpMsg}>
            <option value="">— escolha a mensagem —</option>
            {templates.map(t => <option key={t._id} value={t._id}>{t.titulo}</option>)}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={registrar} onChange={e => setRegistrar(e.target.checked)} />
              Registrar como contato ao abrir
            </label>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{enviados.size} de {comTel.length} enviados</span>
          </div>
          <div style={{ height: 8, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden', marginTop: 8 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#25d366', transition: 'width .2s' }} />
          </div>
          {semTel > 0 && <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>{semTel} cliente(s) sem telefone serão ignorados.</div>}
        </div>

        {tpl && proximo && (
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #f3f4f6', background: '#f0fdf4' }}>
            <button onClick={() => enviar(proximo)} style={{ width: '100%', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Abrir próximo → {primeiroNome(proximo.cliente)}
            </button>
          </div>
        )}
        {tpl && !proximo && comTel.length > 0 && (
          <div style={{ padding: '12px 18px', borderBottom: '1px solid #f3f4f6', background: '#f0fdf4', textAlign: 'center', fontSize: 13, color: '#15803d', fontWeight: 600 }}>
            ✓ Todos os {comTel.length} foram abertos!
          </div>
        )}

        <div style={{ overflowY: 'auto', padding: '6px 0' }}>
          {!tpl ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 24 }}>Escolha uma mensagem acima para ver a fila.</div>
          ) : comTel.map(c => {
            const feito = enviados.has(c.cliente_id);
            const ehProximo = proximo && proximo.cliente_id === c.cliente_id;
            return (
              <div key={c.cliente_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 18px', borderLeft: '3px solid ' + (ehProximo ? '#25d366' : 'transparent'), background: ehProximo ? '#f0fdf4' : '#fff' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0a1628' }}>{c.cliente}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{textoDe(c)}</div>
                </div>
                {feito
                  ? <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>✓ Enviado</span>
                  : <button onClick={() => enviar(c)} style={{ background: ehProximo ? '#25d366' : '#fff', color: ehProximo ? '#fff' : '#374151', border: ehProximo ? 'none' : '1px solid #d1d5db', borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Abrir</button>}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #f3f4f6', textAlign: 'right' }}>
          <button onClick={onFechar} style={btnSec}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ── Relatório de envios ───────────────────────────────
function fmtEnvioDt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}
function pillEnvio(origem) {
  const base = { fontSize: 11, borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap', fontWeight: 600, justifySelf: 'start' };
  if (origem === 'lote') return { ...base, color: '#15803d', background: '#f0fdf4' };
  if (origem === 'direto') return { ...base, color: '#6b7280', background: '#f3f4f6' };
  return { ...base, color: '#1d4ed8', background: '#eff6ff' };
}
function MetricMini({ label, valor, cor, texto }) {
  return (
    <div style={{ flex: 1, minWidth: 110, background: '#f9fafb', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: texto ? 13 : 22, fontWeight: 700, color: cor || '#0a1628', marginTop: texto ? 4 : 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{valor}</div>
    </div>
  );
}

function ModalRelatorio({ templates, onFechar }) {
  const [modo, setModo] = useState('geral');
  const [periodo, setPeriodo] = useState('7');
  const [templateId, setTemplateId] = useState('');
  const [busca, setBusca] = useState('');
  const [data, setData] = useState(null);
  const [expandido, setExpandido] = useState(() => new Set());
  const [baixando, setBaixando] = useState('');

  const de = periodo ? new Date(Date.now() - Number(periodo) * 86400000).toISOString().slice(0, 10) : '';
  const filtrosQS = useCallback(() => {
    const qs = new URLSearchParams();
    if (de) qs.set('de', de);
    if (templateId) qs.set('template_id', templateId);
    if (busca.trim()) qs.set('busca', busca.trim());
    return qs;
  }, [de, templateId, busca]);

  const carregar = useCallback(() => {
    const qs = filtrosQS(); qs.set('_', Date.now());
    fetch(`/sucesso-cliente/envios?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(setData).catch(() => {});
  }, [filtrosQS]);
  useEffect(() => { carregar(); }, [carregar]);

  const baixar = async (formato) => {
    setBaixando(formato);
    try {
      const resp = await fetch(`/sucesso-cliente/envios/export/${formato}?${filtrosQS()}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!resp.ok) throw new Error('falha');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = formato === 'excel' ? 'relatorio_envios.xlsx' : 'relatorio_envios.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { alert('Não foi possível gerar o arquivo.'); }
    setBaixando('');
  };

  const itens = data?.itens || [];
  const resumo = data?.resumo || { total: 0, ultimos_7d: 0, clientes_alcancados: 0, top_mensagem: '—' };
  const porCliente = {};
  itens.forEach(x => { const k = x.cliente_id || x.cliente_nome; (porCliente[k] = porCliente[k] || { nome: x.cliente_nome, itens: [] }).itens.push(x); });
  const gruposCliente = Object.entries(porCliente).map(([k, v]) => ({ k, ...v })).sort((a, b) => b.itens.length - a.itens.length);
  const toggleExp = (k) => setExpandido(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 640, maxWidth: '96%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>📊 Relatório de envios</h2>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '14px 18px 6px', flexWrap: 'wrap' }}>
          <MetricMini label="Total de envios" valor={resumo.total} />
          <MetricMini label="Últimos 7 dias" valor={resumo.ultimos_7d} cor="#16a34a" />
          <MetricMini label="Clientes alcançados" valor={resumo.clientes_alcancados} />
          <MetricMini label="Mensagem + usada" valor={resumo.top_mensagem} texto />
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 18px 12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
            {[['geral', 'Geral'], ['cliente', 'Por cliente']].map(([k, l]) => (
              <button key={k} onClick={() => setModo(k)} style={{ background: modo === k ? '#0a1628' : '#fff', color: modo === k ? '#fff' : '#374151', border: 'none', fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer' }}>{l}</button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ ...inpMsg, width: 'auto', padding: '5px 8px' }}>
            <option value="7">7 dias</option><option value="30">30 dias</option><option value="90">90 dias</option><option value="">Tudo</option>
          </select>
          <select value={templateId} onChange={e => setTemplateId(e.target.value)} style={{ ...inpMsg, width: 'auto', padding: '5px 8px' }}>
            <option value="">Todas as mensagens</option>
            {(templates || []).map(t => <option key={t._id} value={t._id}>{t.titulo}</option>)}
          </select>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente..." style={{ ...inpMsg, width: 150, padding: '5px 8px' }} />
        </div>

        <div style={{ overflowY: 'auto', borderTop: '1px solid #f3f4f6', flex: 1 }}>
          {!data ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>Carregando...</div>
          ) : itens.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>Nenhum envio no período/filtro.</div>
          ) : modo === 'geral' ? (
            itens.map((x, i) => (
              <div key={x._id || i} style={{ display: 'grid', gridTemplateColumns: '92px 1fr auto', gap: 8, padding: '8px 18px', borderBottom: '1px solid #f3f4f6', alignItems: 'center', background: i % 2 ? '#fafafa' : '#fff' }}>
                <span style={{ fontSize: 12, color: '#374151' }}>{fmtEnvioDt(x.criado_em)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0a1628' }}>{x.cliente_nome || '—'}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.texto || '(sem mensagem)'}</div>
                </div>
                <span style={pillEnvio(x.origem)}>{x.template_titulo}</span>
              </div>
            ))
          ) : (
            gruposCliente.map(g => {
              const aberto = expandido.has(g.k);
              return (
                <div key={g.k} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <div onClick={() => toggleExp(g.k)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', cursor: 'pointer' }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', width: 12 }}>{aberto ? '▾' : '▸'}</span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0a1628' }}>{g.nome || '—'}</span>
                    <span style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', borderRadius: 99, padding: '2px 9px', fontWeight: 600 }}>{g.itens.length} envio{g.itens.length !== 1 ? 's' : ''}</span>
                  </div>
                  {aberto && g.itens.map((x, i) => (
                    <div key={x._id || i} style={{ display: 'flex', gap: 10, padding: '6px 18px 6px 38px', borderTop: '1px dashed #f0f0f0', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#6b7280', width: 84, flexShrink: 0 }}>{fmtEnvioDt(x.criado_em)}</span>
                      <span style={pillEnvio(x.origem)}>{x.template_titulo}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.texto || '(sem mensagem)'}</span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 18px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => baixar('pdf')} disabled={!!baixando || itens.length === 0} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (baixando || itens.length === 0) ? 0.5 : 1 }}>{baixando === 'pdf' ? '...' : '📄 PDF'}</button>
            <button onClick={() => baixar('excel')} disabled={!!baixando || itens.length === 0} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (baixando || itens.length === 0) ? 0.5 : 1 }}>{baixando === 'excel' ? '...' : '📊 Excel'}</button>
          </div>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{itens.length} envio(s)</span>
        </div>
      </div>
    </div>
  );
}

const btnSec = { background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' };
const btnWa = { background: '#25d366', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer' };
const btnMarcar = { background: '#0a1628', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' };
const btnCopiar = { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 9px', fontSize: 12, cursor: 'pointer' };
const btnPag = { background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, minWidth: 30, height: 30, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

// ── ⚖️ Demanda em LOTE a partir dos marcos selecionados ─────────────────────
// Um tipo + um prazo (sugerido pela data de publicação da RPI) → cria a mesma
// demanda pra todos os processos marcados, direto na aba MANUAIS.
function ModalDemandaLote({ marcos, selInpi, rpis, onFechar, onSalvo }) {
  const [tipo, setTipo] = useState('');
  const [prazo, setPrazo] = useState('');
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Um item por processo selecionado (o mesmo número pode ter 2 despachos)
  const itens = [];
  const vistos = new Set();
  for (const m of (marcos?.marcos || [])) {
    if (!m.numero_processo || !selInpi.has(m.numero_processo) || vistos.has(m.numero_processo)) continue;
    vistos.add(m.numero_processo);
    itens.push(m);
  }
  const semCliente = itens.filter(m => !m.cliente_id);

  const dataPub = (rpis || []).find(r => String(r.numero) === String(marcos?.rpi))?.data || '';
  function aoTrocarTipo(t) {
    setTipo(t);
    const dias = PRAZO_DIAS[t];
    const m = String(dataPub).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (dias && m) {
      const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
      d.setDate(d.getDate() + dias);
      setPrazo(d.toISOString().slice(0, 10));
    }
  }

  async function lancar() {
    if (!tipo) return setErro('Escolha o tipo da demanda.');
    if (!prazo) return setErro('Informe o prazo.');
    setErro(''); setSalvando(true);
    try {
      const r = await fetch('/demandas/lote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          origem: 'manual',
          demandas: itens.filter(m => m.cliente_id).map(m => ({
            cliente_id: m.cliente_id, cliente_nome: m.cliente || '',
            processo_numero: m.numero_processo, marca: m.marca || '',
            tipo, prazo, observacao: obs,
          })),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Erro ao lançar.');
      alert(`⚖️ ${d.criadas} demanda(s) criada(s) na aba Manuais!${(d.recusadas || []).length ? `\nRecusadas: ${d.recusadas.map(x => x.processo).join(', ')}` : ''}${semCliente.length ? `\nSem cliente cadastrado (puladas): ${semCliente.map(m => m.numero_processo).join(', ')}` : ''}`);
      onSalvo();
    } catch (e) { setErro(e.message); setSalvando(false); }
  }

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 480, maxWidth: '95vw', maxHeight: '88vh', overflowY: 'auto', border: '1px solid #c8d0da' }}>
        <div style={{ background: '#7c3aed', padding: '8px 14px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>⚖️ Demanda em lote — {itens.length} processo(s)</span>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 5, marginBottom: 10 }}>
            {itens.map(m => (
              <div key={m.numero_processo} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                <b style={{ color: '#0a1628' }}>{m.marca || '(sem marca)'}</b>
                <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>{m.numero_processo}</span>
                <span style={{ color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.cliente || ''}</span>
                {!m.cliente_id && <span style={{ color: '#b45309', fontSize: 10.5, fontWeight: 700 }}>sem cliente — pulado</span>}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, marginBottom: 6 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Tipo da demanda *</label>
              <select value={tipo} onChange={e => { aoTrocarTipo(e.target.value); setErro(''); }}
                style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid #b0b8c4', fontSize: 13, background: '#fff' }}>
                <option value="">Escolha…</option>
                {TIPOS_DEMANDA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Prazo *</label>
              <input type="date" value={prazo} onChange={e => { setPrazo(e.target.value); setErro(''); }}
                style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid #b0b8c4', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>
          {dataPub && tipo && PRAZO_DIAS[tipo] ? (
            <div style={{ fontSize: 11, color: '#1d4ed8', marginBottom: 8 }}>📅 Prazo sugerido: publicação {dataPub} + {PRAZO_DIAS[tipo]} dias — vale pra todas; ajuste se precisar.</div>
          ) : null}
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 }}>Observação (vai em todas)</label>
          <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
            placeholder={`Ex.: publicações da RPI ${marcos?.rpi || ''}`}
            style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid #b0b8c4', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
          {erro && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={lancar} disabled={salvando}
              style={{ background: '#7c3aed', color: '#fff', padding: '6px 18px', borderRadius: 5, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>
              {salvando ? 'Lançando…' : `Lançar ${itens.filter(m => m.cliente_id).length} demanda(s)`}
            </button>
            <button onClick={onFechar} style={{ background: '#f3f4f6', color: '#374151', padding: '6px 14px', borderRadius: 5, fontSize: 13, border: '1px solid #d1d5db', cursor: 'pointer' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
